import {
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleX,
  Copy,
  Dices,
  Download,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Plus,
  Recycle,
  Save,
  Server as ServerIcon,
  Sparkles,
  Trash2,
  Upload,
  Volume2,
  VolumeX,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react'
import { createDefaultProfile, DEFAULT_UPSCALE_SETTINGS } from './defaults'
import {
  UpscaleCenter,
  UpscaleQuickPanel,
  WorkspaceModeTabs,
} from './UpscaleWorkspace'
import { ServerStatusStrip } from './ServerStatusStrip'
import { JellyGenerateButton } from './JellyGenerateButton'
import { ComfyClient } from './lib/comfyClient'
import {
  DRAFT_IDLE_SAVE_MS,
  readWorkDraft,
  writeWorkDraft,
  type WorkDraftState,
} from './lib/draft'
import { prepareUploadFile } from './lib/image'
import {
  addStartImage,
  clearDraftEndImages,
  clearStartImageHistory,
  listStartImages,
  loadDraftEndImages,
  loadStartImage,
  pruneDraftEndImages,
  saveDraftEndImage,
  START_IMAGE_HISTORY_LIMIT,
  type StartImageHistoryEntry,
} from './lib/imageHistory'
import { readTrackedPrompts, removeTrackedPrompts, trackPrompt, updateTrackedPrompt } from './lib/jobStore'
import {
  completionTimeFromHistory,
  displayIdentity,
  jobDisplayTitle,
} from './lib/jobPresentation'
import { enableSegmentCustomLora, findPairedLowLora, normalizeLoraRows } from './lib/loraPairing'
import { resolveModelName } from './lib/modelNames'
import { filterByKeywords, filterBySearch } from './lib/models'
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  desktopPermission,
  playCompletionSound,
  readNotificationPreferences,
  requestDesktopPermission,
  saveCustomSound,
  showDesktopCompletion,
  writeNotificationPreferences,
} from './lib/notifications'
import {
  normalizeProfile,
  readProfiles,
  serializeProfile,
  writeProfiles,
} from './lib/profile'
import { describeProgress } from './lib/progress'
import {
  createServer,
  deleteServerModelCache,
  distributeServers,
  loadServerRegistry,
  mergeModelLists,
  persistServerRegistry,
  readServerModelCache,
  retryableServers,
  serverCompatibilityErrors,
  writeServerModelCache,
} from './lib/servers'
import {
  duplicateSegment,
  insertSegmentAfter,
  moveSegment,
  nodeTypeOptionsForPosition,
  removeSegment,
} from './lib/segments'
import { validateGeneration } from './lib/validation'
import { effectivePreserveAudio, inspectMediaSize, inspectMp4Video, type MediaSize, type Mp4VideoInfo } from './lib/mediaTracks'
import { buildConcatWorkflow, buildEnhanceWorkflow, ENHANCE_RIFE_MODEL, ENHANCE_SOURCE_PRESETS, enhanceInterpolationNodes, enhanceLoopFrameLimitExceeded, enhanceMultiplier, enhanceOutputSize, resolveEnhanceSource, runExclusive, resolveEnhanceSubmission, selectConcatBackend, selectGimmModel, selectRifeModel } from './lib/upscaleWorkflow'
import {
  buildSimpleWorkflow,
  buildWorkflow,
  effectiveSeed,
} from './lib/workflow'
import type {
  AppProfile,
  GlobalSettings,
  InspectorTab,
  JobRecord,
  LoraScope,
  LoraSetting,
  ModelLists,
  NotificationPreferences,
  NodeType,
  Segment,
  ServerDefinition,
  ServerRuntime,
  UpscaleTool,
  UpscaleSettings,
  WorkspaceMode,
} from './types'
import './styles.css'

const numberValue = (value: string, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const displayError = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

const optionValue = (value: string): string => value || 'None'
const storedValue = (value: string): string => value === 'None' ? 'None' : value

const modelOptions = (options: string[], current: string): string[] => {
  if (!current || options.includes(current)) return options
  return [current, ...options]
}

const loraCount = (rows: LoraSetting[]): number =>
  rows.filter((row) => row.name && row.name.toLowerCase() !== 'none').length

const resolveGlobalLoras = (profile: AppProfile): Segment[] => {
  const source = profile.mode === 'simple' ? [profile.simple_segment] : profile.segments
  return source.map((segment) => segment.lora_mode === 'global'
    ? {
        ...segment,
        lora_high: normalizeLoraRows(profile.gs.global_lora_high),
        lora_low: normalizeLoraRows(profile.gs.global_lora_low),
      }
    : {
        ...segment,
        lora_high: normalizeLoraRows(segment.lora_high),
        lora_low: normalizeLoraRows(segment.lora_low),
      })
}

const pickFirst = (options: string[], keywords: string | string[], current: string): string => {
  if (current && current !== 'None' && options.includes(current)) return current
  return filterByKeywords(options, keywords, current)[0] ?? current
}

type FieldSelectProps = {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  disabled?: boolean
}

function FieldSelect({ label, value, options, onChange, disabled }: FieldSelectProps) {
  const choices = modelOptions(options, value)
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
        {!value && <option value="">選択してください</option>}
        {choices.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  )
}

type ConnectionBarProps = {
  servers: ServerDefinition[]
  runtimes: Record<string, ServerRuntime>
  target: string
  connecting: boolean
  profiles: Record<string, AppProfile>
  profileName: string
  profileDirty: boolean
  mode: AppProfile['mode']
  onTarget: (value: string) => void
  onConnect: () => void
  onProfileSelect: (value: string) => void
  onSaveProfile: () => void
}

export function ConnectionBar(props: ConnectionBarProps) {
  const visibleProfiles = Object.keys(props.profiles)
    .filter((name) => props.mode === 'simple' ? name.endsWith('_simple') : !name.endsWith('_simple'))
    .sort()
  const enabledServers = props.servers.filter((server) => server.enabled)
  const connectableCount = enabledServers.filter((server) => server.url.trim()).length
  const onlineCount = enabledServers.filter((server) => {
    const status = props.runtimes[server.id]?.status
    return status === 'idle' || status === 'busy'
  }).length
  const retryCount = retryableServers(enabledServers, props.runtimes).length
  return (
    <header className="topbar" aria-label="ComfyUI servers">
      <div className="brand">
        <span className="brand-mark">M</span>
        <span><strong>Mugen 2.0</strong><small>Static ComfyUI Studio</small></span>
      </div>
      <div className="topbar-center">
        <div className="server-status-field">
          <span>Server Status</span>
          <ServerStatusStrip servers={enabledServers} runtimes={props.runtimes} target={props.target} />
        </div>
        <label className="target-field">
          <span>Server Select</span>
          <select aria-label="Server Select" value={props.target} onChange={(event) => props.onTarget(event.target.value)}>
            <option value="auto">Auto</option>
            {enabledServers.map((server) => <option key={server.id} value={server.id}>{server.name}</option>)}
          </select>
        </label>
      </div>
      <div className="topbar-inspector">
        <div className="profile-top">
          <label>
            <span>Profile</span>
            <select value={props.profileName} onChange={(event) => props.onProfileSelect(event.target.value)}>
              <option value="">Current / unsaved</option>
              {visibleProfiles.map((name) => <option key={name} value={name}>{name}{props.profileName === name && props.profileDirty ? ' · unsaved' : ''}</option>)}
            </select>
          </label>
          <button className="icon-button" type="button" aria-label="Save profile" title="Save profile" onClick={props.onSaveProfile}>
            <Save size={16} />
          </button>
        </div>
        <button
          className={`connect-button ${onlineCount ? 'is-connected' : ''}`}
          type="button"
          onClick={props.onConnect}
          disabled={props.connecting || (connectableCount > 0 && retryCount === 0)}
          title={retryCount ? `${retryCount} Offline／Not connected Server を再接続` : undefined}
        >
          {props.connecting
            ? 'Checking…'
            : onlineCount && retryCount
              ? '● Retry Offline'
              : onlineCount
                ? `● ${onlineCount}/${enabledServers.length} Online`
                : '● Connect All'}
        </button>
      </div>
    </header>
  )
}

type QuickPanelProps = {
  profile: AppProfile
  models: ModelLists
  connected: boolean
  startPreview: string
  startName: string
  startHistory: StartImageHistoryEntry[]
  startImageId: string
  historyLoading: boolean
  batch: number
  lastUsedSeed: number | null
  submitting: boolean
  workspaceMode: WorkspaceMode
  onWorkspaceMode: (mode: WorkspaceMode) => void
  onStartImage: (file: File) => void
  onHistoryStep: (direction: -1 | 1) => void
  onClearHistory: () => void
  onGs: (patch: Partial<GlobalSettings>) => void
  onBatch: (value: number) => void
  onGenerate: () => void
}

type SeedControlProps = {
  value: number
  lastUsedSeed: number | null
  onChange: (value: number) => void
}

export function SeedControl({ value, lastUsedSeed, onChange }: SeedControlProps) {
  const [draft, setDraft] = useState(String(value))

  useEffect(() => setDraft(String(value)), [value])

  const changeDraft = (next: string) => {
    if (!/^-?\d*$/.test(next)) return
    setDraft(next)
    if (/^-?\d+$/.test(next)) {
      const parsed = Number(next)
      if (Number.isSafeInteger(parsed)) onChange(parsed)
    }
  }

  return (
    <div className="seed-field">
      <label>
        <span>Seed</span>
        <input
          aria-label="Seed"
          type="text"
          inputMode="numeric"
          spellCheck={false}
          value={draft}
          onChange={(event) => changeDraft(event.target.value)}
          onBlur={() => { if (!/^-?\d+$/.test(draft)) setDraft(String(value)) }}
        />
      </label>
      <button className="icon-button" type="button" aria-label="Random seed" title="Random seed (-1)" onClick={() => onChange(-1)}><Dices size={16} /></button>
      <button className="icon-button" type="button" aria-label="Reuse last generated seed" title="Reuse last generated seed" disabled={lastUsedSeed === null} onClick={() => { if (lastUsedSeed !== null) onChange(lastUsedSeed) }}><Recycle size={16} /></button>
    </div>
  )
}

function QuickPanel(props: QuickPanelProps) {
  const { gs } = props.profile
  const highModels = filterByKeywords(props.models.unet, 'high', gs.unet_high)
  const lowModels = filterByKeywords(props.models.unet, 'low', gs.unet_low)

  const acceptImage = (file?: File) => {
    if (file?.type.startsWith('image/')) props.onStartImage(file)
  }
  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    acceptImage(event.dataTransfer.files[0])
  }
  const historyIndex = props.startHistory.findIndex((entry) => entry.id === props.startImageId)
  const historyPosition = props.historyLoading
    ? '…'
    : props.startHistory.length ? `${historyIndex >= 0 ? historyIndex + 1 : '–'} / ${props.startHistory.length}` : '0 / 0'

  return (
    <aside className="quick-panel" aria-label="Quick controls">
      <div className="quick-fixed-top">
        <WorkspaceModeTabs mode={props.workspaceMode} onMode={props.onWorkspaceMode} />
        <section className="section">
          <div className="section-title start-image-title">
            <span>Start Image <small>必須</small></span>
            <div className="start-image-navigation">
              <button className="icon-button" type="button" aria-label="Previous Start Image" title="Previous Start Image" disabled={props.historyLoading || props.startHistory.length < 2} onClick={() => props.onHistoryStep(-1)}><ChevronLeft size={15} /></button>
              <span className="history-position" aria-label="Start Image history position">{historyPosition}</span>
              <button className="icon-button" type="button" aria-label="Next Start Image" title="Next Start Image" disabled={props.historyLoading || props.startHistory.length < 2} onClick={() => props.onHistoryStep(1)}><ChevronRight size={15} /></button>
            </div>
            <button className="icon-button danger start-history-clear" type="button" aria-label="Clear all Start Image history" title={`Clear all Start Image history（最大 ${START_IMAGE_HISTORY_LIMIT} 張）`} disabled={props.historyLoading || props.startHistory.length === 0} onClick={props.onClearHistory}><Trash2 size={15} /></button>
          </div>
          <label className={`image-drop ${props.startPreview ? 'has-image' : ''}`} onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
            <input type="file" accept="image/*" hidden onChange={(event) => { acceptImage(event.target.files?.[0]); event.currentTarget.value = '' }} />
            {props.startPreview
              ? <img src={props.startPreview} alt={`Start Image: ${props.startName}`} />
              : <span><strong>画像を選択</strong><br /><small>PNG / JPG・ドロップ可</small></span>}
          </label>
        </section>
      </div>

      <div className="quick-scroll">
        <section className="section field-stack">
          <div className="section-title"><span>Quick Tune</span><small>よく使う設定</small></div>
          <FieldSelect label="High Noise Model" value={gs.unet_high} options={highModels} onChange={(unet_high) => props.onGs({ unet_high })} />
          <FieldSelect label="Low Noise Model" value={gs.unet_low} options={lowModels} onChange={(unet_low) => props.onGs({ unet_low })} />
          <div className="field-grid">
            <label>
              <span>動画の長辺</span>
              <select value={gs.resolution} onChange={(event) => props.onGs({ resolution: numberValue(event.target.value, 960) })}>
                {[480, 720, 960, 1080].map((value) => <option key={value}>{value}</option>)}
              </select>
            </label>
            <label>
              <span>Accel LoRA</span>
              <input type="number" min="0" max="5" step="0.05" value={gs.accel_lora_strength} onChange={(event) => props.onGs({ accel_lora_strength: numberValue(event.target.value, 1) })} />
            </label>
            <label>
              <span>CFG</span>
              <input type="number" step="0.1" value={gs.cfg} onChange={(event) => props.onGs({ cfg: numberValue(event.target.value, 1) })} />
            </label>
            <label>
              <span>Shift</span>
              <input type="number" step="0.5" value={gs.shift} onChange={(event) => props.onGs({ shift: numberValue(event.target.value, 8) })} />
            </label>
          </div>
          <div className="hint">長辺のみ指定・Start Image の比率で自動計算</div>
          <SeedControl value={gs.seed} lastUsedSeed={props.lastUsedSeed} onChange={(seed) => props.onGs({ seed })} />
          <div className="hint">全 Segment の High / Low で共通・-1 は Job ごとに一度だけ抽選</div>
        </section>

        <section className="section field-stack">
          <div className="switch-row">
            <label><input type="checkbox" checked={gs.color_match} onChange={(event) => props.onGs({ color_match: event.target.checked })} /> Color Match</label>
          </div>
          {gs.color_match && <div className="field-grid">
            <label>
              <span>Mode</span>
              <select value={gs.color_match_mode} onChange={(event) => props.onGs({ color_match_mode: event.target.value })}>
                {['hm-mkl-hm', 'mkl', 'hm', 'reinhard', 'mvgd'].map((value) => <option key={value}>{value}</option>)}
              </select>
            </label>
            <label>
              <span>Strength</span>
              <input type="number" min="0" max="1" step="0.05" value={gs.color_match_strength} onChange={(event) => props.onGs({ color_match_strength: numberValue(event.target.value, 0.7) })} />
            </label>
          </div>}
        </section>

        <section className="section field-stack">
          <div className="switch-row">
            <label><input type="checkbox" checked={gs.use_rife_bridge} disabled={props.connected && props.models.rife.length === 0} onChange={(event) => props.onGs({ use_rife_bridge: event.target.checked })} /> Loop Bridge（補間）</label>
          </div>
          {props.connected && props.models.rife.length === 0 && <div className="warning-text">RIFE VFI node / model が接続中の Server にありません。</div>}
          {gs.use_rife_bridge && <label>
            <span>補間 Frames</span>
            <input type="number" min="1" max="16" value={gs.rife_bridge_frames} onChange={(event) => props.onGs({ rife_bridge_frames: numberValue(event.target.value, 2) })} />
          </label>}
        </section>
      </div>

      <div className="quick-fixed-bottom">
        <div className="generate-row">
          <JellyGenerateButton onClick={props.onGenerate} disabled={props.submitting}>
            <Sparkles size={16} /> {props.submitting ? 'Submitting…' : 'Generate'}
          </JellyGenerateButton>
          <label className="batch-field"><span>Batch</span><input type="number" min="1" max="99" value={props.batch} onChange={(event) => props.onBatch(Math.max(1, Math.min(99, numberValue(event.target.value, 1))))} /></label>
        </div>
      </div>
    </aside>
  )
}

type TimelineProps = {
  profile: AppProfile
  endFiles: Record<string, File>
  endModes: Record<string, 'start' | 'image'>
  onSegments: (update: (segments: Segment[]) => Segment[]) => void
  onSimple: (segment: Segment) => void
  onOpenLora: (scope: LoraScope) => void
  onEndFile: (segmentId: string, file?: File) => void
  onEndMode: (segmentId: string, mode: 'start' | 'image') => void
}

function SegmentCard({
  segment,
  index,
  total,
  simple,
  endFile,
  endMode,
  onChange,
  onOpenLora,
  onEndFile,
  onEndMode,
  onMove,
  onCopyPrompt,
  onCopyLora,
  onDuplicate,
  onDelete,
}: {
  segment: Segment
  index: number
  total: number
  simple: boolean
  endFile?: File
  endMode: 'start' | 'image'
  onChange: (segment: Segment) => void
  onOpenLora: () => void
  onEndFile: (file?: File) => void
  onEndMode: (mode: 'start' | 'image') => void
  onMove: (direction: -1 | 1) => void
  onCopyPrompt: () => void
  onCopyLora: () => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  const nodeOptions = nodeTypeOptionsForPosition(index, simple)
  const supportsEnd = segment.node_type === 'flf2v' || segment.node_type === 'sviflf'
  const selectedEndMode = endFile || segment.end_image ? 'image' : endMode
  const highCount = loraCount(segment.lora_high)
  const lowCount = loraCount(segment.lora_low)
  const setNodeType = (node_type: NodeType) => {
    const next = { ...segment, node_type }
    if (node_type === 'i2v' || node_type === 'svi') {
      delete next.end_image
      onEndFile(undefined)
      onEndMode('start')
    }
    onChange(next)
  }
  const setEndMode = (value: 'start' | 'image') => {
    onEndMode(value)
    if (value === 'start') {
      const next = { ...segment }
      delete next.end_image
      onEndFile(undefined)
      onChange(next)
    }
  }

  return (
    <article className="segment-card">
      <header className="segment-head">
        <div className="segment-identity">
          <span className="segment-number">#{String(index + 1).padStart(2, '0')}</span>
          <strong>{segment.prompt.trim().split(/[。.!?\n]/)[0] || `Segment ${index + 1}`}</strong>
        </div>
        <div className="segment-actions">
          <button
            className={`lora-badge ${segment.lora_mode === 'custom' ? 'is-custom' : ''}`}
            type="button"
            title={segment.lora_mode === 'custom' ? 'Custom LoRA を編集' : 'この Segment 専用 LoRA を有効化'}
            onClick={onOpenLora}
          >
            {segment.lora_mode === 'custom'
              ? <>Custom LoRA H{highCount}/L{lowCount}</>
              : <><Plus size={13} /> Custom LoRA</>}
          </button>
          {!simple && <>
            <button className="icon-button" type="button" aria-label="Move segment up" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}><ChevronUp size={16} /></button>
            <button className="icon-button" type="button" aria-label="Move segment down" title="Move down" disabled={index === total - 1} onClick={() => onMove(1)}><ChevronDown size={16} /></button>
            {index > 0 && <button className="icon-button source-copy" type="button" aria-label="Copy prompt from first segment" title="Segment 1 の Prompt をコピー" onClick={onCopyPrompt}>P₁</button>}
            {index > 0 && <button className="icon-button source-copy" type="button" aria-label="Copy LoRA from first segment" title="Segment 1 の LoRA をコピー" onClick={onCopyLora}>L₁</button>}
            <button className="icon-button" type="button" aria-label="Duplicate segment" title="Duplicate" onClick={onDuplicate}><Copy size={15} /></button>
            <button className="icon-button danger" type="button" aria-label="Delete segment" title="Delete" disabled={total <= 2} onClick={onDelete}><Trash2 size={15} /></button>
          </>}
        </div>
      </header>
      <div className="segment-body">
        <label>
          <span>Prompt</span>
          <textarea value={segment.prompt} onChange={(event) => onChange({ ...segment, prompt: event.target.value })} placeholder="この Segment の動きと画面を記述…" />
        </label>
        <div className="segment-meta">
          <label><span>Frames</span><input type="number" min="1" value={segment.frame_count} onChange={(event) => onChange({ ...segment, frame_count: Math.max(1, numberValue(event.target.value, 81)) })} /></label>
          <label><span>Node type</span><select value={segment.node_type} onChange={(event) => setNodeType(event.target.value as NodeType)}>{nodeOptions.map((node) => <option key={node} value={node}>{node.toUpperCase()}</option>)}</select></label>
          <label className="wide">
            <span>End Image</span>
            <select value={supportsEnd ? selectedEndMode : 'none'} disabled={!supportsEnd} onChange={(event) => setEndMode(event.target.value as 'start' | 'image')}>
              {supportsEnd ? <><option value="start">Start Frame を使用</option><option value="image">画像を指定</option></> : <option value="none">指定しない</option>}
            </select>
            {segment.node_type === 'sviflf' && <small>シーン転換は FLF2V 推奨</small>}
          </label>
          {supportsEnd && selectedEndMode === 'image' && <label className="wide compact-file">
            <input type="file" accept="image/*" onChange={(event) => onEndFile(event.target.files?.[0])} />
            <small>{endFile?.name ?? segment.end_image ?? '画像を選択してください'}</small>
          </label>}
        </div>
      </div>
    </article>
  )
}

function Timeline(props: TimelineProps) {
  const simple = props.profile.mode === 'simple'
  const segments = simple ? [props.profile.simple_segment] : props.profile.segments
  const frames = segments.reduce((sum, segment) => sum + segment.frame_count, 0)
  const updateAt = (segment: Segment) => {
    if (simple) props.onSimple(segment)
    else props.onSegments((segments) => segments.map((current) => current.id === segment.id ? segment : current))
  }
  const copyPromptFromFirst = (segmentId: string) => {
    props.onSegments((segments) => {
      const source = segments[0]
      return source ? segments.map((segment) => segment.id === segmentId ? { ...segment, prompt: source.prompt } : segment) : segments
    })
  }
  const copyLoraFromFirst = (segmentId: string) => {
    props.onSegments((segments) => {
      const source = segments[0]
      if (!source) return segments
      const sourceHigh = source.lora_mode === 'global' ? props.profile.gs.global_lora_high : source.lora_high
      const sourceLow = source.lora_mode === 'global' ? props.profile.gs.global_lora_low : source.lora_low
      return segments.map((segment) => segment.id === segmentId ? {
        ...segment,
        lora_mode: 'custom',
        lora_high: structuredClone(sourceHigh),
        lora_low: structuredClone(sourceLow),
      } : segment)
    })
  }
  const openSegmentCustomLora = (segment: Segment) => {
    const replacement = enableSegmentCustomLora(
      segment,
      props.profile.gs.global_lora_high,
      props.profile.gs.global_lora_low,
    )
    if (replacement !== segment) updateAt(replacement)
    props.onOpenLora({ kind: 'segment', segmentId: segment.id })
  }
  return (
    <section className="timeline" aria-label="Segment timeline">
      <div className="timeline-toolbar">
        <div className="timeline-title"><h2>{simple ? 'Simple Shot' : 'Segment Timeline'}</h2><span>{segments.length} segment{segments.length === 1 ? '' : 's'}・{frames} frames</span></div>
        <div className="toolbar-actions">
          <button className="global-lora-button" type="button" onClick={() => props.onOpenLora({ kind: 'global' })}>Global LoRA</button>
          {!simple && <button type="button" onClick={() => props.onSegments((current) => insertSegmentAfter(current, current.length - 1))}><Plus size={16} /> Segment</button>}
        </div>
      </div>
      <div className="segment-list">
        {segments.map((segment, index) => <SegmentCard
          key={segment.id}
          segment={segment}
          index={index}
          total={segments.length}
          simple={simple}
          endFile={props.endFiles[segment.id]}
          endMode={props.endModes[segment.id] ?? (segment.end_image ? 'image' : 'start')}
          onChange={updateAt}
          onOpenLora={() => openSegmentCustomLora(segment)}
          onEndFile={(file) => props.onEndFile(segment.id, file)}
          onEndMode={(mode) => props.onEndMode(segment.id, mode)}
          onMove={(direction) => props.onSegments((current) => {
            const currentIndex = current.findIndex((item) => item.id === segment.id)
            return currentIndex < 0 ? current : moveSegment(current, currentIndex, direction)
          })}
          onCopyPrompt={() => copyPromptFromFirst(segment.id)}
          onCopyLora={() => copyLoraFromFirst(segment.id)}
          onDuplicate={() => props.onSegments((current) => {
            const currentIndex = current.findIndex((item) => item.id === segment.id)
            return currentIndex < 0 ? current : duplicateSegment(current, currentIndex)
          })}
          onDelete={() => {
            props.onEndFile(segment.id, undefined)
            props.onSegments((current) => {
              const currentIndex = current.findIndex((item) => item.id === segment.id)
              return currentIndex < 0 ? current : removeSegment(current, currentIndex)
            })
          }}
        />)}
      </div>
    </section>
  )
}

type LoraEditorProps = {
  profile: AppProfile
  models: ModelLists
  scope: LoraScope
  autoPair: boolean
  onAutoPair: (value: boolean) => void
  onProfile: (profile: AppProfile) => void
  onReturnGlobal: () => void
}

export function LoraEditor(props: LoraEditorProps) {
  const [searches, setSearches] = useState<Record<string, string>>({})
  const findSegment = (): Segment | null => {
    if (props.scope.kind === 'global') return null
    const all = [...props.profile.segments, props.profile.simple_segment]
    return all.find((segment) => segment.id === props.scope.segmentId) ?? null
  }
  const segment = findSegment()
  const scopeKey = props.scope.kind === 'global' ? 'global' : props.scope.segmentId ?? 'segment'
  const search = searches[scopeKey] ?? ''
  const highRows = props.scope.kind === 'global' ? props.profile.gs.global_lora_high : segment?.lora_high ?? []
  const lowRows = props.scope.kind === 'global' ? props.profile.gs.global_lora_low : segment?.lora_low ?? []
  const rowCount = Math.max(1, Math.min(4, Math.max(highRows.length, lowRows.length)))
  const highs = normalizeLoraRows(highRows, rowCount)
  const lows = normalizeLoraRows(lowRows, rowCount)
  const highOptions = filterBySearch(props.models.lora, search, highs.map((row) => row.name))
  const lowOptions = filterBySearch(props.models.lora, search, lows.map((row) => row.name))

  const commit = (nextHigh: LoraSetting[], nextLow: LoraSetting[], loraMode?: 'global' | 'custom') => {
    if (props.scope.kind === 'global') {
      props.onProfile({ ...props.profile, gs: { ...props.profile.gs, global_lora_high: nextHigh, global_lora_low: nextLow } })
      return
    }
    if (!segment) return
    const replacement = { ...segment, lora_high: nextHigh, lora_low: nextLow, lora_mode: loraMode ?? segment.lora_mode }
    if (props.profile.simple_segment.id === segment.id) props.onProfile({ ...props.profile, simple_segment: replacement })
    else props.onProfile({ ...props.profile, segments: props.profile.segments.map((item) => item.id === segment.id ? replacement : item) })
  }
  const changeHigh = (index: number, patch: Partial<LoraSetting>) => {
    const nextHigh = highs.map((row, current) => current === index ? { ...row, ...patch } : row)
    let nextLow = lows
    if (props.autoPair && patch.name) {
      const paired = findPairedLowLora(patch.name, props.models.lora)
      if (paired) nextLow = lows.map((row, current) => current === index ? { ...row, name: paired } : row)
    }
    commit(nextHigh, nextLow)
  }
  const changeLow = (index: number, patch: Partial<LoraSetting>) => commit(highs, lows.map((row, current) => current === index ? { ...row, ...patch } : row))
  const removePair = (index: number) => commit(highs.filter((_, current) => current !== index), lows.filter((_, current) => current !== index))
  const addPair = () => {
    if (rowCount >= 4) return
    commit([...highs, { name: '', strength: 1 }], [...lows, { name: '', strength: 1 }])
  }

  return (
    <div className="lora-editor">
      <div className="section-title"><span>{props.scope.kind === 'global'
        ? 'Global LoRA'
        : props.profile.simple_segment.id === segment?.id
          ? 'Simple Custom LoRA'
          : `Segment #${props.profile.segments.findIndex((item) => item.id === segment?.id) + 1} Custom LoRA`}</span><small>High / Low 同時確認</small></div>
      {segment && <div className="custom-lora-toolbar">
        <span>この Segment のみ適用中</span>
        <button type="button" onClick={() => {
          commit(highs, lows, 'global')
          props.onReturnGlobal()
        }}>Global に戻す</button>
      </div>}
      <label className="lora-search"><span>LoRA Search</span><input value={search} placeholder="この Scope の候補を絞り込み…" onChange={(event) => setSearches((current) => ({ ...current, [scopeKey]: event.target.value }))} /></label>
      <div className="switch-row auto-pair"><label><input type="checkbox" checked={props.autoPair} onChange={(event) => props.onAutoPair(event.target.checked)} /> High → Low 自動ペア</label></div>
      <div className="lora-zone">
        <h3>High Noise</h3>
        {highs.map((row, index) => <div className="lora-row" key={`high-${index}`}>
          <span>{index + 1}</span>
          <select value={optionValue(row.name)} onChange={(event) => changeHigh(index, { name: storedValue(event.target.value) })}>{modelOptions(highOptions, optionValue(row.name)).map((name) => <option key={name || 'None'} value={name || 'None'}>{name || 'None'}</option>)}</select>
          <input aria-label={`High LoRA ${index + 1} strength`} type="number" min="0" max="5" step="0.05" value={row.strength} onChange={(event) => changeHigh(index, { strength: numberValue(event.target.value, 1) })} />
          <button className="icon-button danger" type="button" aria-label={`Remove LoRA pair ${index + 1}`} disabled={rowCount <= 1} onClick={() => removePair(index)}><Trash2 size={14} /></button>
        </div>)}
      </div>
      <div className="lora-zone low-zone">
        <h3>Low Noise</h3>
        {lows.map((row, index) => <div className="lora-row" key={`low-${index}`}>
          <span>{index + 1}</span>
          <select value={optionValue(row.name)} onChange={(event) => changeLow(index, { name: storedValue(event.target.value) })}>{modelOptions(lowOptions, optionValue(row.name)).map((name) => <option key={name || 'None'} value={name || 'None'}>{name || 'None'}</option>)}</select>
          <input aria-label={`Low LoRA ${index + 1} strength`} type="number" min="0" max="5" step="0.05" value={row.strength} onChange={(event) => changeLow(index, { strength: numberValue(event.target.value, 1) })} />
          <span className="row-spacer" />
        </div>)}
      </div>
      <button className="add-pair" type="button" disabled={rowCount >= 4} onClick={addPair}><Plus size={16} /> LoRA Pair</button>
      <p className="hint">自動ペアは HighNoise→LowNoise、_High→_Low、-H→-L の完全一致だけを使用します。</p>
    </div>
  )
}

type ResultPanelProps = {
  connected: boolean
  jobs: JobRecord[]
  activeJobId: string
  compactPreview: boolean
  onSelect: (job: JobRecord) => void
  onDownload: (job: JobRecord) => void
  onCancel: (job: JobRecord) => void
  onClearFinished: () => void
  onCompactPreview: (value: boolean) => void
}

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return '0:00'
  const whole = Math.max(0, Math.floor(seconds))
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

const isFinishedJob = (job: JobRecord): boolean =>
  job.status === 'completed' || job.status === 'cancelled' || job.status === 'error'

export function ResultPanel(props: ResultPanelProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const viewerRef = useRef<HTMLDivElement>(null)
  const [playing, setPlaying] = useState(true)
  const [fullscreen, setFullscreen] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [videoAspect, setVideoAspect] = useState<number | null>(null)
  const [volume, setVolume] = useState(.7)
  const [muted, setMuted] = useState(true)
  const completed = [...props.jobs]
    .filter((job) => job.status === 'completed' && job.media)
    .sort((a, b) => a.createdAt - b.createdAt)
  const requestedIndex = completed.findIndex((job) => job.id === props.activeJobId)
  const activeIndex = requestedIndex >= 0 ? requestedIndex : Math.max(0, completed.length - 1)
  const activeJob = completed.find((job) => job.id === props.activeJobId) ?? completed.at(-1)
  const activeUrl = activeJob?.mediaUrl ?? ''
  const activeDescription = activeJob ? `${activeJob.label ?? 'Generation'}・${activeJob.serverName ?? 'ComfyUI'}` : ''
  const activeJobNumber = activeJob
    ? Math.max(1, props.jobs.findIndex((job) => job.id === activeJob.id) + 1)
    : 0
  const activeTitle = activeJob ? jobDisplayTitle(activeJob, activeJobNumber) : ''
  const isImage = activeJob?.media?.kind === 'image' || /\.(?:png|jpe?g|webp|gif)$/i.test(activeJob?.media?.filename ?? '')
  useEffect(() => {
    setVideoAspect(null)
    setTime(0)
    setDuration(0)
  }, [activeUrl])
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume
      videoRef.current.muted = muted
    }
  }, [activeUrl, muted, volume])
  useEffect(() => {
    const syncFullscreen = () => setFullscreen(document.fullscreenElement === viewerRef.current)
    document.addEventListener('fullscreenchange', syncFullscreen)
    syncFullscreen()
    return () => document.removeEventListener('fullscreenchange', syncFullscreen)
  }, [])
  const toggle = async () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) await video.play()
    else video.pause()
  }
  const toggleFullscreen = async () => {
    const viewer = viewerRef.current
    if (!viewer) return
    if (document.fullscreenElement === viewer) await document.exitFullscreen()
    else await viewer.requestFullscreen()
  }
  const stepResult = (direction: -1 | 1) => {
    if (completed.length < 2) return
    const next = (activeIndex + direction + completed.length) % completed.length
    props.onSelect(completed[next])
  }
  const finishedCount = props.jobs.filter(isFinishedJob).length
  return (
    <div className="result-panel">
      <div className="result-status">
        <span className={props.connected ? 'status-dot connected' : 'status-dot'} />
        <span className="result-connection-label">{props.connected ? 'ComfyUI connected' : 'Not connected'}</span>
        {activeDescription && <small className="result-active-description" title={activeDescription}>・{activeDescription}</small>}
      </div>
      <div className="result-header">
        <strong className="result-title">Result Monitor</strong>
        <div className="result-history" aria-label="Result history">
          <button className="icon-button" type="button" aria-label="Previous result" disabled={completed.length < 2} onClick={() => stepResult(-1)}><ChevronLeft size={15} /></button>
          <span className="result-history-position">{completed.length ? `${activeIndex + 1} / ${completed.length}` : '0 / 0'}</span>
          <button className="icon-button" type="button" aria-label="Next result" disabled={completed.length < 2} onClick={() => stepResult(1)}><ChevronRight size={15} /></button>
        </div>
        <div className="result-actions">
          <label className="result-preview-lock" title="プレビューサイズを16:9に固定">
            <input aria-label="Lock result preview to 16:9" type="checkbox" checked={props.compactPreview} onChange={(event) => props.onCompactPreview(event.target.checked)} />
            <span aria-hidden="true">{props.compactPreview ? '🔒' : '🔓'}</span>
          </label>
          <button className="icon-button result-download" type="button" aria-label="Download current result" title="Download current result" disabled={!activeJob?.media} onClick={() => { if (activeJob) props.onDownload(activeJob) }}><Download size={17} /></button>
        </div>
      </div>
      <div className="result-current-title" title={activeTitle} aria-live="polite">{activeTitle}</div>
      <div
        className={`result-viewer ${activeUrl ? 'has-video' : ''} ${props.compactPreview ? 'is-compact' : ''}`}
        ref={viewerRef}
        style={{ aspectRatio: props.compactPreview ? '16 / 9' : videoAspect ? String(videoAspect) : '16 / 9' }}
      >
        {activeUrl
          ? isImage ? <img className="result-image" src={activeUrl} alt={activeJob?.media?.filename ?? 'Enhanced result'} onLoad={(event) => { const image = event.currentTarget; if (image.naturalWidth && image.naturalHeight) setVideoAspect(image.naturalWidth / image.naturalHeight) }} /> : <video
              key={activeUrl}
              ref={videoRef}
              src={activeUrl}
              crossOrigin="use-credentials"
              autoPlay
              loop
              muted={muted}
              playsInline
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
              onLoadedMetadata={(event) => {
                const video = event.currentTarget
                setDuration(video.duration)
                if (video.videoWidth > 0 && video.videoHeight > 0) {
                  setVideoAspect(video.videoWidth / video.videoHeight)
                }
              }}
            />
          : <div className="result-placeholder"><span>16:9 Result Monitor</span><small>完成した動画はここで自動ループ再生します</small></div>}
        <button
          className="fullscreen-button"
          type="button"
          aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          onClick={() => void toggleFullscreen()}
        >{fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}</button>
      </div>
      <div className="player-controls">
        <button className="icon-button" type="button" aria-label={playing ? 'Pause' : 'Play'} disabled={!activeUrl || isImage} onClick={() => void toggle()}>{playing ? <Pause size={16} /> : <Play size={16} />}</button>
        <span>{formatTime(time)}</span>
        <input aria-label="Video position" type="range" min="0" max={Math.max(duration, 0.01)} step="0.01" value={Math.min(time, duration || 0)} disabled={!activeUrl || isImage} onChange={(event) => { if (videoRef.current) videoRef.current.currentTime = numberValue(event.target.value, 0) }} />
        <span>{formatTime(duration)}</span>
        <div className="result-volume-control">
          <button className="icon-button" type="button" aria-label={muted ? 'Unmute result' : 'Mute result'} disabled={!activeUrl || isImage} onClick={() => setMuted((current) => !current)}>{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
          <div className="volume-popover"><input aria-label="Result volume" type="range" min="0" max="1" step="0.01" value={volume} onChange={(event) => { setVolume(numberValue(event.target.value, .7)); setMuted(false) }} /></div>
        </div>
      </div>
      <section className="queue-section">
        <div className="queue-header">
          <div><strong>Jobs</strong><small>{props.jobs.length} tracked</small></div>
          <button className="clear-jobs-button" type="button" disabled={!finishedCount} onClick={props.onClearFinished}><Trash2 size={14} /> Clear finished</button>
        </div>
        <div className="queue-list">
          {props.jobs.length === 0 && <p className="empty-state">送信した Job はここに表示されます。</p>}
          {[...props.jobs].sort((a, b) => b.createdAt - a.createdAt).map((job, index) => (
            <div className="queue-item" key={job.id}>
              <div className="job-copy">
                <strong title={jobDisplayTitle(job, props.jobs.length - index)}>{jobDisplayTitle(job, props.jobs.length - index)}</strong>
                {(job.recovered || job.serverName) && <small className="job-server-line">{job.recovered ? 'Recovered・' : ''}{job.serverName ?? 'ComfyUI'}</small>}
                {job.label
                  ? <small>{job.segmentTotal} source{job.segmentTotal === 1 ? '' : 's'}</small>
                  : <small className="job-seed-line">Seed {job.effectiveSeed >= 0 ? job.effectiveSeed : 'unknown'}・all H/L</small>}
                {job.status === 'running' && <small>{describeProgress(job.currentNode, job.stepValue, job.stepMax, job.segmentTotal)}</small>}
                {job.error && <details className="job-error-details">
                  <summary><span className="job-error-summary">{job.error}</span></summary>
                  <div className="job-error-expanded">
                    <pre className="job-error-full">{job.error}</pre>
                    <button className="job-error-copy" type="button" aria-label="Copy full error" title="Copy full error" onClick={() => void navigator.clipboard?.writeText(job.error ?? '').catch(() => undefined)}>
                      <Copy size={13} aria-hidden="true" /> Copy
                    </button>
                  </div>
                </details>}
              </div>
              <div className="queue-actions">
                <span className={`job-status ${job.status}`}>{job.status === 'completed' ? 'Completed' : job.status === 'running' ? `${Math.round(job.progress)}%` : job.status}</span>
                {job.status === 'completed' && job.media && <button
                    className="icon-button download-button"
                    type="button"
                    aria-label={`Download Job ${props.jobs.length - index}`}
                    title="Download"
                    onClick={() => props.onDownload(job)}
                  ><Download size={17} /></button>}
                {(job.status === 'queued' || job.status === 'running') && job.promptId && <button
                    className="icon-button cancel-job-button"
                    type="button"
                    aria-label={`Cancel Job ${props.jobs.length - index}`}
                    title={job.status === 'running' ? 'Cancel running Job (cannot resume)' : 'Remove Job from queue'}
                    onClick={() => props.onCancel(job)}
                  ><CircleX size={17} /></button>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

type ServerEditorCardProps = {
  server: ServerDefinition
  index: number
  total: number
  runtime?: ServerRuntime
  apiKey: string
  onServer: (id: string, patch: Partial<ServerDefinition>) => void
  onMoveServer: (id: string, direction: -1 | 1) => void
  onDeleteServer: (id: string) => void
  onApiKey: (id: string, value: string) => void
  onConnectServer: (id: string) => void
}

export function ServerEditorCard(props: ServerEditorCardProps) {
  const connectionDirty = useRef(false)
  const finishConnectionEdit = () => {
    if (!connectionDirty.current) return
    connectionDirty.current = false
    props.onConnectServer(props.server.id)
  }
  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget as Node | null
    if (next && event.currentTarget.contains(next)) return
    finishConnectionEdit()
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' || !connectionDirty.current) return
    event.preventDefault()
    finishConnectionEdit()
  }
  const connectionEdit = (action: () => void) => {
    connectionDirty.current = true
    action()
  }
  const { server, runtime } = props

  return <section className="server-editor-card" onBlur={handleBlur} onKeyDown={handleKeyDown}>
    <div className="server-editor-header">
      <span className={`server-source is-${server.source}`}>{server.source}</span>
      <span className={`server-editor-status is-${runtime?.status ?? 'disconnected'}`}>{runtime?.status ?? 'disconnected'}</span>
      <span className="server-editor-spacer" />
      <button className="icon-button" type="button" aria-label={`Move ${server.name} up`} disabled={props.index === 0} onClick={() => props.onMoveServer(server.id, -1)}><ChevronUp size={15} /></button>
      <button className="icon-button" type="button" aria-label={`Move ${server.name} down`} disabled={props.index === props.total - 1} onClick={() => props.onMoveServer(server.id, 1)}><ChevronDown size={15} /></button>
      {server.source !== 'company' && <button className="icon-button danger-icon" type="button" aria-label={`Delete ${server.name}`} onClick={() => props.onDeleteServer(server.id)}><Trash2 size={15} /></button>}
    </div>
    <div className="switch-row"><label><input type="checkbox" checked={server.enabled} onChange={(event) => connectionEdit(() => props.onServer(server.id, { enabled: event.target.checked }))} /> Enabled</label></div>
    <label><span>Name</span><input value={server.name} disabled={server.source === 'company'} onChange={(event) => props.onServer(server.id, { name: event.target.value })} /></label>
    <label><span>ComfyUI URL</span><input type="url" autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" value={server.url} disabled={server.source === 'company'} placeholder="https://... / http://127.0.0.1:8188" onChange={(event) => connectionEdit(() => props.onServer(server.id, { url: event.target.value }))} /></label>
    {server.source !== 'company' && <label><span>Storage</span><select value={server.source} onChange={(event) => props.onServer(server.id, { source: event.target.value === 'temporary' ? 'temporary' : 'personal' })}><option value="personal">Personal・browser</option><option value="temporary">Temporary・this tab</option></select></label>}
    <label><span>API key・session only</span><input className="api-key-input" type="text" name={`mugen-server-token-${server.id}`} autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" value={props.apiKey} onChange={(event) => connectionEdit(() => props.onApiKey(server.id, event.target.value))} /></label>
    {runtime?.error && <p className="warning-text">{runtime.error}</p>}
  </section>
}

type SettingsPanelProps = {
  profile: AppProfile
  models: ModelLists
  connected: boolean
  profileName: string
  profiles: Record<string, AppProfile>
  notifications: NotificationPreferences
  servers: ServerDefinition[]
  runtimes: Record<string, ServerRuntime>
  apiKeys: Record<string, string>
  onGs: (patch: Partial<GlobalSettings>) => void
  onNotifications: (patch: Partial<NotificationPreferences>) => void
  onTestSound: () => void
  onCustomSound: (file: File) => void
  onDesktopEnabled: (enabled: boolean) => void
  onAddServer: (source: 'personal' | 'temporary') => void
  onServer: (id: string, patch: Partial<ServerDefinition>) => void
  onMoveServer: (id: string, direction: -1 | 1) => void
  onDeleteServer: (id: string) => void
  onApiKey: (id: string, value: string) => void
  onConnectServer: (id: string) => void
  onDeleteProfile: () => void
  onImportProfile: (file: File) => void
  onExportProfile: () => void
}

function SettingsPanel(props: SettingsPanelProps) {
  const [tab, setTab] = useState<'models' | 'workflow' | 'output' | 'notify' | 'servers' | 'profile'>(() => props.servers.length ? 'models' : 'servers')
  const { gs } = props.profile
  const permission = desktopPermission()
  return (
    <div className="settings-panel">
      <div className="settings-tabs">
        {(['models', 'workflow', 'output', 'notify', 'servers', 'profile'] as const).map((name) => <button type="button" key={name} className={tab === name ? 'is-active' : ''} onClick={() => setTab(name)}>{name[0].toUpperCase() + name.slice(1)}</button>)}
      </div>
      {tab === 'models' && <div className="settings-group field-stack">
        <h3>Low-frequency Models</h3>
        <FieldSelect label="Text Encoder" value={gs.clip} options={filterByKeywords(props.models.clip, 'umt5', gs.clip)} onChange={(clip) => props.onGs({ clip })} />
        <FieldSelect label="VAE" value={gs.vae} options={filterByKeywords(props.models.vae, 'WAN_2.1', gs.vae)} onChange={(vae) => props.onGs({ vae })} />
        <FieldSelect label="CLIP Vision" value={gs.clip_vision} options={filterByKeywords(props.models.clipVision, 'CLIP-ViT-H', gs.clip_vision)} onChange={(clip_vision) => props.onGs({ clip_vision })} />
      </div>}
      {tab === 'workflow' && <div className="settings-group field-stack">
        <h3>Workflow LoRA</h3>
        <FieldSelect label="Accel LoRA Model" value={gs.accel_lora} options={filterByKeywords(props.models.lora, 'lightx2v_I2V_14B_480p_cfg_step_distill', gs.accel_lora)} onChange={(accel_lora) => props.onGs({ accel_lora })} />
        <FieldSelect label="SVI High" value={gs.svi_lora_high} options={filterByKeywords(props.models.lora, ['SVI', 'high'], gs.svi_lora_high)} onChange={(svi_lora_high) => props.onGs({ svi_lora_high })} />
        <FieldSelect label="SVI Low" value={gs.svi_lora_low} options={filterByKeywords(props.models.lora, ['SVI', 'low'], gs.svi_lora_low)} onChange={(svi_lora_low) => props.onGs({ svi_lora_low })} />
        <label><span>SVI Strength</span><input type="number" step="0.05" value={gs.svi_lora_strength} onChange={(event) => props.onGs({ svi_lora_strength: numberValue(event.target.value, 1) })} /></label>
        <div className="field-grid">
          <label><span>Total Steps</span><input type="number" min="1" value={gs.total_steps} onChange={(event) => props.onGs({ total_steps: numberValue(event.target.value, 6) })} /></label>
          <label><span>High Steps</span><input type="number" min="1" value={gs.high_steps} onChange={(event) => props.onGs({ high_steps: numberValue(event.target.value, 2) })} /></label>
          <FieldSelect label="Sampler" value={gs.sampler} options={props.models.samplers} onChange={(sampler) => props.onGs({ sampler })} />
          <FieldSelect label="Scheduler" value={gs.scheduler} options={props.models.schedulers} onChange={(scheduler) => props.onGs({ scheduler })} />
        </div>
        <p className="hint">未接続時は基本候補、接続後は ComfyUI の KSamplerAdvanced から取得します。</p>
      </div>}
      {tab === 'output' && <div className="settings-group field-stack">
        <h3>Output / Combine</h3>
        <div className="field-grid">
          <label><span>Combine FPS</span><input type="number" min="1" value={gs.combine_fps} onChange={(event) => props.onGs({ combine_fps: numberValue(event.target.value, 16) })} /></label>
          <label><span>Overlap</span><input type="number" min="0" value={gs.overlap} onChange={(event) => props.onGs({ overlap: numberValue(event.target.value, 5) })} /></label>
        </div>
        <FieldSelect label="RIFE Model" value={gs.rife_model} options={props.models.rife} disabled={props.connected && props.models.rife.length === 0} onChange={(rife_model) => props.onGs({ rife_model })} />
        {props.connected && props.models.rife.length === 0 && <p className="warning-text">RIFE VFI node / model が見つからないため Loop Bridge は使用できません。</p>}
        <label><span>Negative Prompt</span><textarea value={gs.negative_prompt} onChange={(event) => props.onGs({ negative_prompt: event.target.value })} /></label>
      </div>}
      {tab === 'notify' && <div className="settings-group field-stack">
        <h3><Bell size={16} /> Notifications</h3>
        <div className="switch-row">
          <label><input type="checkbox" checked={props.notifications.soundEnabled} onChange={(event) => props.onNotifications({ soundEnabled: event.target.checked })} /> Completion sound</label>
        </div>
        <label>
          <span>Sound</span>
          <select value={props.notifications.soundSource} onChange={(event) => props.onNotifications({ soundSource: event.target.value === 'custom' ? 'custom' : 'default' })}>
            <option value="default">Default・kakinn.mp3</option>
            <option value="custom" disabled={!props.notifications.customSoundName}>Custom{props.notifications.customSoundName ? `・${props.notifications.customSoundName}` : ''}</option>
          </select>
        </label>
        <label>
          <span>Volume・{Math.round(props.notifications.volume * 100)}%</span>
          <input type="range" min="0" max="1" step="0.05" value={props.notifications.volume} onChange={(event) => props.onNotifications({ volume: numberValue(event.target.value, 0.7) })} />
        </label>
        <div className="notification-actions">
          <label className="file-button"><Upload size={15} /> Custom sound<input type="file" accept="audio/*,.mp3,.wav,.ogg" hidden onChange={(event) => event.target.files?.[0] && props.onCustomSound(event.target.files[0])} /></label>
          <button type="button" onClick={props.onTestSound}><Volume2 size={15} /> Test Sound</button>
        </div>
        <div className="switch-row">
          <label><input type="checkbox" checked={props.notifications.desktopEnabled} disabled={permission === 'unsupported' || permission === 'denied'} onChange={(event) => props.onDesktopEnabled(event.target.checked)} /> Desktop notification</label>
          <small>{permission === 'unsupported' ? '未対応' : permission === 'granted' ? '許可済み' : permission === 'denied' ? 'ブラウザで拒否済み' : '許可が必要'}</small>
        </div>
        <p className="hint">各 Job 完了時に通知し、短時間内の複数完了は 1 回にまとめます。ブラウザを閉じている間は通知できません。</p>
      </div>}
      {tab === 'servers' && <div className="settings-group field-stack">
        <h3><ServerIcon size={16} /> ComfyUI Servers</h3>
        <p className="hint">Company は Mugen 共通設定、Personal はこの browser、Temporary はこの tab だけに保存します。API key は保存しません。</p>
        {props.servers.length === 0 && <p className="empty-state">Server がありません。Local または RunPod を追加してください。</p>}
        <div className="server-list-editor">
          {props.servers.map((server, index) => <ServerEditorCard
            key={server.id}
            server={server}
            index={index}
            total={props.servers.length}
            runtime={props.runtimes[server.id]}
            apiKey={props.apiKeys[server.id] ?? ''}
            onServer={props.onServer}
            onMoveServer={props.onMoveServer}
            onDeleteServer={props.onDeleteServer}
            onApiKey={props.onApiKey}
            onConnectServer={props.onConnectServer}
          />)}
        </div>
        <div className="server-add-actions">
          <button type="button" onClick={() => props.onAddServer('personal')}><Plus size={15} /> Add Personal</button>
          <button type="button" onClick={() => props.onAddServer('temporary')}><Plus size={15} /> Add Temporary</button>
        </div>
      </div>}
      {tab === 'profile' && <div className="settings-group field-stack">
        <h3>Profile portability</h3>
        <p className="hint">Profile は Cloudflare Access identity ごとに browser localStorage へ保存します。Server list と API key は含みません。</p>
        <label className="file-button"><Upload size={16} /> Import JSON<input type="file" accept="application/json,.json" hidden onChange={(event) => event.target.files?.[0] && props.onImportProfile(event.target.files[0])} /></label>
        <button type="button" onClick={props.onExportProfile}>Export current JSON</button>
        <button className="danger-button" type="button" disabled={!props.profileName || !props.profiles[props.profileName]} onClick={props.onDeleteProfile}>Delete selected profile</button>
      </div>}
    </div>
  )
}

type InspectorProps = {
  tab: InspectorTab
  onTab: (tab: InspectorTab) => void
  profile: AppProfile
  models: ModelLists
  loraScope: LoraScope
  autoPair: boolean
  connected: boolean
  jobs: JobRecord[]
  activeJobId: string
  compactResultPreview: boolean
  profileName: string
  profiles: Record<string, AppProfile>
  notifications: NotificationPreferences
  servers: ServerDefinition[]
  runtimes: Record<string, ServerRuntime>
  apiKeys: Record<string, string>
  onProfile: (profile: AppProfile) => void
  onGs: (patch: Partial<GlobalSettings>) => void
  onAutoPair: (value: boolean) => void
  onReturnGlobal: () => void
  onSelectResult: (job: JobRecord) => void
  onDownload: (job: JobRecord) => void
  onCancelJob: (job: JobRecord) => void
  onClearFinishedJobs: () => void
  onCompactResultPreview: (value: boolean) => void
  onNotifications: (patch: Partial<NotificationPreferences>) => void
  onTestSound: () => void
  onCustomSound: (file: File) => void
  onDesktopEnabled: (enabled: boolean) => void
  onAddServer: (source: 'personal' | 'temporary') => void
  onServer: (id: string, patch: Partial<ServerDefinition>) => void
  onMoveServer: (id: string, direction: -1 | 1) => void
  onDeleteServer: (id: string) => void
  onApiKey: (id: string, value: string) => void
  onConnectServer: (id: string) => void
  onDeleteProfile: () => void
  onImportProfile: (file: File) => void
  onExportProfile: () => void
}

function Inspector(props: InspectorProps) {
  return (
    <aside className="inspector" aria-label="Inspector">
      <div className="inspector-fixed-tabs tabbar">
        {(['result', 'lora', 'settings'] as const).map((tab) => <button type="button" key={tab} aria-selected={props.tab === tab} onClick={() => props.onTab(tab)}>{tab === 'result' ? 'Result' : tab === 'lora' ? 'LoRA' : 'Settings'}</button>)}
      </div>
      <div className={`inspector-scroll ${props.tab === 'result' ? 'result-scroll' : ''}`}>
        {props.tab === 'result' && <ResultPanel connected={props.connected} jobs={props.jobs} activeJobId={props.activeJobId} compactPreview={props.compactResultPreview} onSelect={props.onSelectResult} onDownload={props.onDownload} onCancel={props.onCancelJob} onClearFinished={props.onClearFinishedJobs} onCompactPreview={props.onCompactResultPreview} />}
        {props.tab === 'lora' && <LoraEditor profile={props.profile} models={props.models} scope={props.loraScope} autoPair={props.autoPair} onAutoPair={props.onAutoPair} onProfile={props.onProfile} onReturnGlobal={props.onReturnGlobal} />}
        {props.tab === 'settings' && <SettingsPanel profile={props.profile} models={props.models} connected={props.connected} profileName={props.profileName} profiles={props.profiles} notifications={props.notifications} servers={props.servers} runtimes={props.runtimes} apiKeys={props.apiKeys} onGs={props.onGs} onNotifications={props.onNotifications} onTestSound={props.onTestSound} onCustomSound={props.onCustomSound} onDesktopEnabled={props.onDesktopEnabled} onAddServer={props.onAddServer} onServer={props.onServer} onMoveServer={props.onMoveServer} onDeleteServer={props.onDeleteServer} onApiKey={props.onApiKey} onConnectServer={props.onConnectServer} onDeleteProfile={props.onDeleteProfile} onImportProfile={props.onImportProfile} onExportProfile={props.onExportProfile} />}
      </div>
    </aside>
  )
}

function SystemBar({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false)
  const text = message || 'Ready'
  const level = /failed|error|rejected|してください|ありません|失敗/i.test(text)
    ? 'error'
    : /warning|注意|確認/i.test(text)
      ? 'warning'
      : message
        ? 'success'
        : 'idle'

  useEffect(() => setExpanded(false), [message])

  return (
    <footer className="system-bar">
      <div className="system-message-wrap">
        <button
          className={`system-message ${level}`}
          type="button"
          aria-expanded={expanded}
          title="Click to show the complete latest message"
          onClick={() => setExpanded((current) => !current)}
        >
          <span className="system-dot" aria-hidden="true" />
          <span className="system-message-text" aria-live="polite">{text.replace(/\s+/g, ' ')}</span>
        </button>
        {expanded && <div className={`system-detail ${level}`} role="status">{text}</div>}
      </div>
      <div className="author-credit">
        <span>Built by </span>
        <a href="https://github.com/Cyborg968" target="_blank" rel="noreferrer">Cyborg968 <span aria-hidden="true">↗</span></a>
        <span className="author-separator">·</span>
        <span>Discord @cyborg968</span>
      </div>
    </footer>
  )
}

export default function App() {
  const [profile, setProfile] = useState<AppProfile>(() => createDefaultProfile())
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>('multi')
  const [upscaleTool, setUpscaleTool] = useState<UpscaleTool>('enhance')
  const [upscaleSettings, setUpscaleSettings] = useState<UpscaleSettings>({ ...DEFAULT_UPSCALE_SETTINGS })
  const [enhanceFile, setEnhanceFile] = useState<File | null>(null)
  const enhanceDetectionRef = useRef<{ file: File; info: Promise<Mp4VideoInfo | null>; manual: boolean } | null>(null)
  useEffect(() => {
    if (!enhanceFile || enhanceFile.type.startsWith('image/')) { enhanceDetectionRef.current = null; return }
    const detection = { file: enhanceFile, info: inspectMp4Video(enhanceFile), manual: false }
    enhanceDetectionRef.current = detection
    void detection.info.then((info) => {
      if (enhanceDetectionRef.current !== detection) return
      setUpscaleSettings((current) => ({
        ...current,
        enhanceSource: resolveEnhanceSource({ manual: detection.manual, info, selected: current.enhanceSource }),
      }))
    })
  }, [enhanceFile])
  const [enhanceSourceSize, setEnhanceSourceSize] = useState<MediaSize | null>(null)
  useEffect(() => {
    setEnhanceSourceSize(null)
    if (!enhanceFile) return
    let cancelled = false
    void inspectMediaSize(enhanceFile).then((size) => { if (!cancelled) setEnhanceSourceSize(size) })
    return () => { cancelled = true }
  }, [enhanceFile])
  const [concatFiles, setConcatFiles] = useState<Array<File | null>>([null, null])
  const [servers, setServers] = useState<ServerDefinition[]>([])
  const [runtimes, setRuntimes] = useState<Record<string, ServerRuntime>>({})
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({})
  const [target, setTarget] = useState('auto')
  const [submitting, setSubmitting] = useState(false)
  const [batch, setBatch] = useState(1)
  const [notice, setNotice] = useState('')
  const [startFile, setStartFile] = useState<File | null>(null)
  const [startPreview, setStartPreview] = useState('')
  const [startHistory, setStartHistory] = useState<StartImageHistoryEntry[]>([])
  const [startImageId, setStartImageId] = useState('')
  const [historyLoading, setHistoryLoading] = useState(false)
  const [endFiles, setEndFiles] = useState<Record<string, File>>({})
  const [endModes, setEndModes] = useState<Record<string, 'start' | 'image'>>({})
  const [tab, setTab] = useState<InspectorTab>('result')
  const [loraScope, setLoraScope] = useState<LoraScope>({ kind: 'global' })
  const [autoPair, setAutoPair] = useState(true)
  const [jobs, setJobs] = useState<JobRecord[]>([])
  const [lastUsedSeed, setLastUsedSeed] = useState<number | null>(null)
  const [activeJobId, setActiveJobId] = useState('')
  const [compactResultPreview, setCompactResultPreview] = useState(true)
  const [identity, setIdentity] = useState('local')
  const [identityReady, setIdentityReady] = useState(false)
  const [workspaceHydrated, setWorkspaceHydrated] = useState(false)
  const [profiles, setProfiles] = useState<Record<string, AppProfile>>({})
  const [profileName, setProfileName] = useState('')
  const [notifications, setNotifications] = useState<NotificationPreferences>({ ...DEFAULT_NOTIFICATION_PREFERENCES })
  const clients = useRef(new Map<string, ComfyClient>())
  const monitorControllers = useRef(new Map<string, AbortController>())
  const runtimeRef = useRef(runtimes)
  const apiKeysRef = useRef(apiKeys)
  const autoConnectIdentity = useRef('')
  const notificationRef = useRef(notifications)
  const completionTimer = useRef(0)
  const pendingCompletions = useRef<{ count: number; servers: string[] }>({ count: 0, servers: [] })
  const historySelectionToken = useRef(0)
  const draftTimer = useRef(0)
  const draftSnapshotRef = useRef<WorkDraftState | null>(null)
  const draftObservedIdentity = useRef('')
  const draftLastSaved = useRef('')
  const draftDirty = useRef(false)
  const forceNextDraftSave = useRef(false)

  runtimeRef.current = runtimes
  apiKeysRef.current = apiKeys
  notificationRef.current = notifications

  const draftState = useMemo<WorkDraftState>(() => ({
    profile,
    profileName,
    batch,
    target,
    tab,
    loraScope,
    autoPair,
    startImageId,
    endModes,
    lastUsedSeed,
    workspaceMode,
    upscaleTool,
    upscaleSettings,
    compactResultPreview,
  }), [autoPair, batch, compactResultPreview, endModes, lastUsedSeed, loraScope, profile, profileName, startImageId, tab, target, upscaleSettings, upscaleTool, workspaceMode])
  const serializedDraftState = useMemo(() => JSON.stringify(draftState), [draftState])
  draftSnapshotRef.current = draftState

  const models = useMemo(() => {
    const enabledServerIds = new Set(servers.filter((server) => server.enabled).map((server) => server.id))
    return mergeModelLists(
      Object.entries(runtimes)
        .filter(([id, runtime]) => enabledServerIds.has(id) && runtime.models)
        .map(([, runtime]) => runtime.models!),
    )
  }, [runtimes, servers])
  const connected = Object.values(runtimes).some((runtime) => runtime.status === 'idle' || runtime.status === 'busy')
  const connecting = Object.values(runtimes).some((runtime) => runtime.status === 'connecting')
  const profileDirty = useMemo(() => Boolean(
    profileName
    && profiles[profileName]
    && serializeProfile(profile) !== serializeProfile(profiles[profileName]),
  ), [profile, profileName, profiles])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      let resolvedIdentity = 'local'
      try {
        const response = await fetch('/cdn-cgi/access/get-identity')
        if (!response.ok) throw new Error('Access identity unavailable')
        const data = await response.json() as { email?: string }
        if (data.email) resolvedIdentity = data.email
      } catch {
        // Local development intentionally uses its own browser namespace.
      }
      if (!cancelled) {
        setIdentity(resolvedIdentity)
        setIdentityReady(true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!identityReady) return
    let cancelled = false
    setWorkspaceHydrated(false)
    setHistoryLoading(true)
    void (async () => {
      const storedProfiles = readProfiles(identity)
      const storedDraft = readWorkDraft(identity)
      const restoredProfile = storedDraft?.profile ?? createDefaultProfile()
      const allSegmentIds = [...restoredProfile.segments, restoredProfile.simple_segment].map((segment) => segment.id)
      const validSegmentIds = new Set(allSegmentIds)
      const [loadedServers, imageEntries, restoredEndFiles] = await Promise.all([
        loadServerRegistry(identity),
        listStartImages(identity).catch(() => []),
        storedDraft ? loadDraftEndImages(identity, allSegmentIds).catch(() => ({})) : Promise.resolve({}),
      ])
      const requestedStartEntry = storedDraft
        ? imageEntries.find((entry) => entry.id === storedDraft.startImageId)
        : imageEntries.at(-1)
      const restoredStartFile = requestedStartEntry
        ? await loadStartImage(identity, requestedStartEntry).catch(() => null)
        : null
      if (cancelled) return
      const customSegmentIds = new Set(
        [...restoredProfile.segments, restoredProfile.simple_segment]
          .filter((segment) => segment.lora_mode === 'custom')
          .map((segment) => segment.id),
      )
      const restoredScope = storedDraft?.loraScope.kind === 'segment'
        && storedDraft.loraScope.segmentId
        && validSegmentIds.has(storedDraft.loraScope.segmentId)
        && customSegmentIds.has(storedDraft.loraScope.segmentId)
        ? storedDraft.loraScope
        : { kind: 'global' as const }
      setProfiles(storedProfiles)
      const cachedModels = readServerModelCache(identity, loadedServers)
      setNotifications(readNotificationPreferences(identity))
      setServers(loadedServers)
      setRuntimes(Object.fromEntries(loadedServers.map((server) => [server.id, {
        status: 'disconnected' as const,
        running: 0,
        pending: 0,
        models: cachedModels[server.id],
      }])))
      setProfile(restoredProfile)
      setWorkspaceMode(storedDraft?.workspaceMode ?? restoredProfile.mode)
      setUpscaleTool(storedDraft?.upscaleTool ?? 'enhance')
      setUpscaleSettings(storedDraft?.upscaleSettings ?? { ...DEFAULT_UPSCALE_SETTINGS })
      setProfileName(storedDraft?.profileName && storedProfiles[storedDraft.profileName] ? storedDraft.profileName : '')
      setBatch(storedDraft?.batch ?? 1)
      setTarget(storedDraft?.target !== 'auto' && loadedServers.some((server) => server.id === storedDraft?.target && server.enabled)
        ? storedDraft!.target
        : 'auto')
      setTab(storedDraft?.tab ?? 'result')
      setLoraScope(restoredScope)
      setAutoPair(storedDraft?.autoPair ?? true)
      setLastUsedSeed(storedDraft?.lastUsedSeed ?? null)
      setCompactResultPreview(storedDraft?.compactResultPreview ?? true)
      setEndModes(Object.fromEntries(Object.entries(storedDraft?.endModes ?? {}).filter(([id]) => validSegmentIds.has(id))))
      setEndFiles(restoredEndFiles)
      setStartHistory(imageEntries)
      setStartImageId(restoredStartFile && requestedStartEntry ? requestedStartEntry.id : '')
      setStartFile(restoredStartFile)
      setHistoryLoading(false)
      setWorkspaceHydrated(true)
      void pruneDraftEndImages(identity, allSegmentIds).catch(() => undefined)
    })().catch((error) => {
      if (!cancelled) {
        setHistoryLoading(false)
        setWorkspaceHydrated(true)
        setNotice(`Workspace restore failed: ${displayError(error)}`)
      }
    })
    return () => { cancelled = true }
  }, [identity, identityReady])

  const persistDraft = useCallback((reportError = true) => {
    if (!identityReady || !workspaceHydrated || !draftSnapshotRef.current) return
    try {
      writeWorkDraft(identity, draftSnapshotRef.current)
      draftLastSaved.current = JSON.stringify(draftSnapshotRef.current)
      draftDirty.current = false
      window.clearTimeout(draftTimer.current)
      const segmentIds = [...draftSnapshotRef.current.profile.segments, draftSnapshotRef.current.profile.simple_segment]
        .map((segment) => segment.id)
      void pruneDraftEndImages(identity, segmentIds).catch(() => undefined)
    } catch (error) {
      if (reportError) setNotice(`Workspace auto-save failed: ${displayError(error)}`)
    }
  }, [identity, identityReady, workspaceHydrated])

  useEffect(() => {
    window.clearTimeout(draftTimer.current)
    if (!identityReady || !workspaceHydrated) return
    if (draftObservedIdentity.current !== identity) {
      draftObservedIdentity.current = identity
      draftLastSaved.current = serializedDraftState
      draftDirty.current = false
      forceNextDraftSave.current = false
      return
    }
    if (serializedDraftState === draftLastSaved.current) return
    draftDirty.current = true
    if (forceNextDraftSave.current) {
      forceNextDraftSave.current = false
      persistDraft()
      return
    }
    draftTimer.current = window.setTimeout(() => persistDraft(), DRAFT_IDLE_SAVE_MS)
    return () => window.clearTimeout(draftTimer.current)
  }, [identity, identityReady, persistDraft, serializedDraftState, workspaceHydrated])

  useEffect(() => {
    const saveOnPageHide = () => {
      if (draftDirty.current && identityReady && workspaceHydrated && draftSnapshotRef.current) {
        try {
          writeWorkDraft(identity, draftSnapshotRef.current)
        } catch {
          // The page is leaving, so there is no reliable place to report this failure.
        }
      }
    }
    window.addEventListener('pagehide', saveOnPageHide)
    return () => window.removeEventListener('pagehide', saveOnPageHide)
  }, [identity, identityReady, workspaceHydrated])

  useEffect(() => () => {
    monitorControllers.current.forEach((controller) => controller.abort())
    monitorControllers.current.clear()
    clients.current.forEach((client) => client.configure('', ''))
    clients.current.clear()
    window.clearTimeout(completionTimer.current)
    window.clearTimeout(draftTimer.current)
  }, [])

  useEffect(() => {
    if (target !== 'auto' && !servers.some((server) => server.id === target && server.enabled)) setTarget('auto')
  }, [servers, target])

  useEffect(() => {
    if (!startFile) {
      setStartPreview('')
      return
    }
    const url = URL.createObjectURL(startFile)
    setStartPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [startFile])

  const updateGs = (patch: Partial<GlobalSettings>) => setProfile((current) => ({ ...current, gs: { ...current.gs, ...patch } }))

  const clientFor = useCallback((server: ServerDefinition): ComfyClient => {
    let client = clients.current.get(server.id)
    if (!client) {
      client = new ComfyClient()
      clients.current.set(server.id, client)
    }
    client.configure(server.url, apiKeysRef.current[server.id] ?? '')
    return client
  }, [])

  const refreshServerRuntime = useCallback(async (server: ServerDefinition) => {
    if (!server.enabled || !server.url) return
    const client = clientFor(server)
    try {
      const queue = await client.getQueueSnapshot()
      setRuntimes((current) => ({ ...current, [server.id]: {
        status: queue.running.length || queue.pending.length ? 'busy' : 'idle',
        running: queue.running.length,
        pending: queue.pending.length,
        runningJobs: queue.running,
        pendingJobs: queue.pending,
        models: current[server.id]?.models,
      }}))
    } catch (error) {
      setRuntimes((current) => ({ ...current, [server.id]: {
        status: 'offline', running: 0, pending: 0, runningJobs: [], pendingJobs: [],
        models: current[server.id]?.models,
        error: displayError(error),
      }}))
    }
  }, [clientFor])

  useEffect(() => {
    const timer = window.setInterval(() => {
      servers.filter((server) => server.enabled).forEach((server) => {
        const status = runtimeRef.current[server.id]?.status
        if (status === 'idle' || status === 'busy') void refreshServerRuntime(server)
      })
    }, 8000)
    return () => window.clearInterval(timer)
  }, [refreshServerRuntime, servers])

  const queueCompletionNotification = useCallback((serverName: string) => {
    pendingCompletions.current.count += 1
    pendingCompletions.current.servers.push(serverName)
    window.clearTimeout(completionTimer.current)
    completionTimer.current = window.setTimeout(() => {
      const completed = pendingCompletions.current
      pendingCompletions.current = { count: 0, servers: [] }
      const preferences = notificationRef.current
      void playCompletionSound(identity, preferences)
        .catch((error) => setNotice(`Sound failed: ${displayError(error)}`))
      if (preferences.desktopEnabled) showDesktopCompletion(completed.count, completed.servers)
    }, 1800)
  }, [identity])

  const monitorJob = useCallback(async (
    jobId: string,
    promptId: string,
    segmentTotal: number,
    serverId: string,
    serverName: string,
  ) => {
    const server = servers.find((item) => item.id === serverId)
    if (!server) return
    const client = clientFor(server)
    monitorControllers.current.get(jobId)?.abort()
    const controller = new AbortController()
    monitorControllers.current.set(jobId, controller)
    try {
      const history = await client.monitorPrompt(promptId, (update) => {
        setJobs((current) => current.map((job) => job.id === jobId ? {
          ...job,
          status: 'running',
          currentNode: update.currentNode ?? job.currentNode,
          stepValue: update.stepValue ?? job.stepValue,
          stepMax: update.stepMax ?? job.stepMax,
          progress: update.stepMax ? (update.stepValue ?? 0) / update.stepMax * 100 : job.progress,
        } : job))
      }, controller.signal)
      const media = client.extractMedia(history, promptId)
      if (!media) throw new Error('Completed history に出力メディアが見つかりません。')
      const mediaUrl = await client.playableUrl(media)
      const completedAt = completionTimeFromHistory(history, promptId) ?? Date.now()
      updateTrackedPrompt(identity, serverId, promptId, { completedAt })
      setJobs((current) => current.map((job) => job.id === jobId ? { ...job, status: 'completed', progress: 100, media, mediaUrl, completedAt } : job))
      setActiveJobId(jobId)
      setNotice(`Job completed・${serverName}・${segmentTotal} segment${segmentTotal === 1 ? '' : 's'}`)
      queueCompletionNotification(serverName)
    } catch (error) {
      if (controller.signal.aborted) return
      setJobs((current) => current.map((job) => job.id === jobId ? { ...job, status: 'error', error: displayError(error) } : job))
    } finally {
      if (monitorControllers.current.get(jobId) === controller) monitorControllers.current.delete(jobId)
      void refreshServerRuntime(server)
    }
  }, [clientFor, identity, queueCompletionNotification, refreshServerRuntime, servers])

  const recoverJobsForServer = useCallback(async (server: ServerDefinition, client: ComfyClient) => {
    try {
      const tracked = readTrackedPrompts(identity).filter((item) => item.serverId === server.id)
      if (!tracked.length) return
      const queue = await client.getQueuePromptIds()
      const recoveredJobs = (await Promise.all(tracked.map(async (item): Promise<JobRecord | null> => {
        const activeStatus = queue.running.includes(item.promptId)
          ? 'running'
          : queue.pending.includes(item.promptId) ? 'queued' : null
        const metadata = {
          sourceName: item.sourceName,
          sourceCount: item.sourceCount,
          jobKind: item.jobKind,
          batchIndex: item.batchIndex,
          batchTotal: item.batchTotal,
        }
        if (activeStatus) return {
          id: crypto.randomUUID(), promptId: item.promptId, status: activeStatus, progress: 0,
          currentNode: '', stepValue: 0, stepMax: 1, effectiveSeed: item.effectiveSeed,
          segmentTotal: item.segmentTotal, createdAt: item.createdAt, recovered: true,
          serverId: server.id, serverName: server.name,
          ...metadata, completedAt: item.completedAt,
        }
        const history = await client.getHistory(item.promptId)
        const media = client.extractMedia(history, item.promptId)
        if (!media) return null
        const completedAt = item.completedAt ?? completionTimeFromHistory(history, item.promptId)
        if (completedAt && !item.completedAt) updateTrackedPrompt(identity, server.id, item.promptId, { completedAt })
        return {
          id: crypto.randomUUID(), promptId: item.promptId, status: 'completed', progress: 100,
          currentNode: '', stepValue: 0, stepMax: 1, effectiveSeed: item.effectiveSeed,
          segmentTotal: item.segmentTotal, createdAt: item.createdAt, media, recovered: true,
          serverId: server.id, serverName: server.name,
          ...metadata, completedAt,
        }
      }))).filter((job): job is JobRecord => Boolean(job))
      setJobs((current) => {
        const known = new Set(current.map((job) => `${job.serverId}:${job.promptId}`))
        return [...current, ...recoveredJobs.filter((job) => !known.has(`${job.serverId}:${job.promptId}`))]
      })
      const latestCompleted = [...recoveredJobs]
        .sort((a, b) => b.createdAt - a.createdAt)
        .find((job) => job.status === 'completed' && job.media)
      if (latestCompleted?.media) {
        const url = await client.playableUrl(latestCompleted.media)
        latestCompleted.mediaUrl = url
        setJobs((current) => current.map((job) => job.id === latestCompleted.id ? { ...job, mediaUrl: url } : job))
        setActiveJobId(latestCompleted.id)
      }
      recoveredJobs.filter((job) => job.promptId && (job.status === 'running' || job.status === 'queued')).forEach((job) => {
        void monitorJob(job.id, job.promptId!, job.segmentTotal, server.id, server.name)
      })
    } catch {
      // Recovery only reads prompt ids owned by this browser and remains best effort.
    }
  }, [identity, monitorJob])

  const applyAvailableModels = useCallback((availableLists: ModelLists[]) => {
    if (!availableLists.length) return
    const lists = mergeModelLists(availableLists)
    setUpscaleSettings((current) => ({
      ...current,
      upscaleModel: resolveModelName(current.upscaleModel, lists.upscale ?? []) ?? lists.upscale?.[0] ?? current.upscaleModel,
    }))
    setProfile((current) => ({ ...current, gs: {
      ...current.gs,
      unet_high: pickFirst(lists.unet, 'high', current.gs.unet_high),
      unet_low: pickFirst(lists.unet, 'low', current.gs.unet_low),
      clip: pickFirst(lists.clip, 'umt5', current.gs.clip),
      vae: pickFirst(lists.vae, 'WAN_2.1', current.gs.vae),
      clip_vision: pickFirst(lists.clipVision, 'CLIP-ViT-H', current.gs.clip_vision),
      accel_lora: pickFirst(lists.lora, 'lightx2v_I2V_14B_480p_cfg_step_distill', current.gs.accel_lora),
      svi_lora_high: pickFirst(lists.lora, ['SVI', 'high'], current.gs.svi_lora_high),
      svi_lora_low: pickFirst(lists.lora, ['SVI', 'low'], current.gs.svi_lora_low),
      rife_model: pickFirst(lists.rife, 'rife49', current.gs.rife_model),
      sampler: pickFirst(lists.samplers, 'uni_pc', current.gs.sampler),
      scheduler: pickFirst(lists.schedulers, 'simple', current.gs.scheduler),
      use_rife_bridge: lists.rife.length ? current.gs.use_rife_bridge : false,
    }}))
  }, [])

  const connectServer = useCallback(async (
    server: ServerDefinition,
    probeTimeoutMs = 5_000,
  ): Promise<{ serverId: string; models: ModelLists } | null> => {
    if (!server.enabled || !server.url.trim()) return null
    setRuntimes((current) => ({ ...current, [server.id]: {
      ...current[server.id],
      status: 'connecting',
      running: 0,
      pending: 0,
      runningJobs: [],
      pendingJobs: [],
      error: undefined,
    }}))
    const client = clientFor(server)
    try {
      await client.testConnection(probeTimeoutMs)
      const [lists, queue] = await Promise.all([client.getModelLists(), client.getQueueSnapshot()])
      writeServerModelCache(identity, server, lists)
      setRuntimes((current) => ({ ...current, [server.id]: {
        status: queue.running.length || queue.pending.length ? 'busy' : 'idle',
        running: queue.running.length,
        pending: queue.pending.length,
        runningJobs: queue.running,
        pendingJobs: queue.pending,
        models: lists,
      }}))
      await recoverJobsForServer(server, client)
      return { serverId: server.id, models: lists }
    } catch (error) {
      setRuntimes((current) => ({ ...current, [server.id]: {
        ...current[server.id],
        status: 'offline',
        running: 0,
        pending: 0,
        runningJobs: [],
        pendingJobs: [],
        error: displayError(error),
      }}))
      return null
    }
  }, [clientFor, identity, recoverJobsForServer])

  const connectServers = useCallback(async (
    selectedServers: ServerDefinition[],
    quiet = false,
  ) => {
    if (!selectedServers.length) return
    const results = await Promise.all(selectedServers.map((server) => connectServer(server)))
    const available = results.filter((result): result is { serverId: string; models: ModelLists } => Boolean(result))
    const freshModels = new Map(available.map((result) => [result.serverId, result.models]))
    const participatingIds = new Set([
      ...servers.filter((server) => server.enabled).map((server) => server.id),
      ...selectedServers.map((server) => server.id),
    ])
    const availableModels = [...participatingIds].flatMap((id) => {
      const fresh = freshModels.get(id)
      if (fresh) return [fresh]
      const cached = runtimeRef.current[id]?.models
      return cached ? [cached] : []
    })
    applyAvailableModels(availableModels)
    if (quiet) return
    const offline = selectedServers.length - available.length
    if (available.length) {
      setNotice(`${available.length}/${selectedServers.length} Servers connected・capability lists loaded${offline ? `・${offline} offline` : ''}`)
    } else {
      setNotice('Connection failed・選択した ComfyUI Server は Offline です。')
    }
  }, [applyAvailableModels, connectServer, servers])

  const connect = async () => {
    const enabledServers = servers.filter((server) => server.enabled && server.url.trim())
    if (!enabledServers.length) {
      setNotice('Settings › Servers で ComfyUI Server を追加してください。')
      setTab('settings')
      return
    }
    const selectedServers = retryableServers(enabledServers, runtimeRef.current)
    if (!selectedServers.length) {
      setNotice('すべての有効な Server は接続済みです。')
      return
    }
    setNotice('')
    await connectServers(selectedServers)
  }

  const connectServerById = useCallback((id: string) => {
    const server = servers.find((item) => item.id === id)
    if (!server || !server.enabled || !server.url.trim()) return
    if (jobs.some((job) => job.serverId === id && (job.status === 'submitting' || job.status === 'queued' || job.status === 'running'))) {
      setNotice(`Server '${server.name}' に active Job があるため再接続できません。`)
      return
    }
    const status = runtimeRef.current[id]?.status
    if (status === 'connecting') return
    void connectServers([server])
  }, [connectServers, jobs, servers])

  useEffect(() => {
    if (!identityReady || !workspaceHydrated || autoConnectIdentity.current === identity) return
    autoConnectIdentity.current = identity
    const candidates = retryableServers(servers, runtimeRef.current)
    if (candidates.length) void connectServers(candidates, true)
  }, [connectServers, identity, identityReady, servers, workspaceHydrated])

  const cancelJob = async (job: JobRecord) => {
    if (!job.promptId || (job.status !== 'queued' && job.status !== 'running')) return
    const server = servers.find((item) => item.id === job.serverId)
    if (!server) {
      setNotice('Cancel failed: Job の Server 設定が見つかりません。')
      return
    }
    try {
      const client = clientFor(server)
      const action = await client.cancelPrompt(job.promptId)
      monitorControllers.current.get(job.id)?.abort()
      setJobs((current) => current.map((item) => item.id === job.id
        ? { ...item, status: 'cancelled', error: undefined }
        : item))
      setNotice(action === 'interrupted'
        ? 'Job cancelled・ComfyUI の現在の生成を中断しました（再開できません）'
        : 'Job cancelled・ComfyUI の待機キューから削除しました')
      removeTrackedPrompts(identity, [job.promptId])
      void refreshServerRuntime(server)
    } catch (error) {
      setNotice(`Cancel failed: ${displayError(error)}`)
    }
  }

  const clearFinishedJobs = () => {
    const finishedPromptIds = jobs.filter(isFinishedJob).flatMap((job) => job.promptId ? [job.promptId] : [])
    jobs.filter(isFinishedJob).forEach((job) => { if (job.mediaUrl?.startsWith('blob:')) URL.revokeObjectURL(job.mediaUrl) })
    removeTrackedPrompts(identity, finishedPromptIds)
    setJobs((current) => current.filter((job) => !isFinishedJob(job)))
    setActiveJobId('')
    setNotice('Finished Jobs cleared')
  }

  const upscalePreparingRef = useRef(false)
  // Probes run before `submitting` is set; the lock keeps a second click from queueing the same job twice.
  const generateUpscale = () => runExclusive(upscalePreparingRef, submitUpscale)
  const submitUpscale = async () => {
    const sourceFiles = upscaleTool === 'enhance'
      ? enhanceFile ? [enhanceFile] : []
      : concatFiles.filter((file): file is File => Boolean(file))
    if (upscaleTool === 'enhance' && sourceFiles.length !== 1) {
      setNotice('Enhance の画像または MP4 を 1 件選択してください。')
      return
    }
    if (upscaleTool === 'concat' && (sourceFiles.length < 2 || sourceFiles.length > 8)) {
      setNotice('Concat は MP4 を 2～8 本選択してください。')
      return
    }
    if (!connected) {
      setNotice('ComfyUI Server に接続してください。')
      return
    }
    const enhanceKind = sourceFiles[0]?.type.startsWith('image/') ? 'image' : 'video'
    // Generate can be pressed before the FPS probe settles; bind this file's probe before the first await.
    const enhanceSubmission = upscaleTool === 'enhance' && enhanceKind === 'video'
      ? resolveEnhanceSubmission(
          enhanceDetectionRef.current?.file === sourceFiles[0] ? enhanceDetectionRef.current : null,
          upscaleSettings.enhanceSource,
          () => inspectMp4Video(sourceFiles[0]),
        )
      : null
    const enhanceSizeProbe = upscaleTool === 'enhance' && upscaleSettings.scaleMode === 'longEdge'
      ? inspectMediaSize(sourceFiles[0])
      : null
    const preserveEnhanceAudio = upscaleTool === 'enhance' && enhanceKind === 'video'
      ? await effectivePreserveAudio(sourceFiles[0], upscaleSettings.preserveAudio)
      : false
    const enhanceTargetSize = enhanceSizeProbe ? enhanceOutputSize(await enhanceSizeProbe, upscaleSettings) : null
    if (enhanceSizeProbe && !enhanceTargetSize) {
      setNotice('ソースのサイズを取得できません。Final Scale を倍率に切り替えてください。')
      return
    }
    const { info: enhanceVideoInfo, enhanceSource } = enhanceSubmission
      ? await enhanceSubmission
      : { info: null, enhanceSource: upscaleSettings.enhanceSource }
    const { sourceFps, interpolator } = ENHANCE_SOURCE_PRESETS[enhanceSource]
    const enhanceMultiplierValue = enhanceMultiplier(upscaleSettings.targetFps, sourceFps)
    const interpolationNodes = upscaleTool === 'enhance' && enhanceKind === 'video' && enhanceMultiplierValue > 1
      ? enhanceInterpolationNodes(interpolator)
      : []
    if (interpolationNodes.length && upscaleSettings.enhanceLoop
      && enhanceLoopFrameLimitExceeded(enhanceVideoInfo, Math.max(1, Math.round(upscaleSettings.targetFps)) / enhanceMultiplierValue)) {
      setNotice('ループ補正は 4096 フレームまでの動画に対応しています。ループ補正をオフにしてください。')
      return
    }
    const selectedServers = target === 'auto'
      ? servers.filter((server) => server.enabled)
      : servers.filter((server) => server.id === target && server.enabled)
    const requiredNodes = upscaleTool === 'concat'
      ? []
      : enhanceKind === 'image'
        ? ['UpscaleModelLoader', 'ImageUpscaleWithModel', 'ImageScaleBy']
        : ['VHS_LoadVideo', 'UpscaleModelLoader', 'ImageUpscaleWithModel', 'ImageScaleBy', 'VHS_VideoCombine', ...interpolationNodes]
    const reports = selectedServers.map((server) => {
      const runtime = runtimes[server.id]
      const online = runtime?.status === 'idle' || runtime?.status === 'busy'
      const resolvedUpscaleModel = online && runtime.models
        ? resolveModelName(upscaleSettings.upscaleModel, runtime.models.upscale ?? [])
        : null
      const concatBackend = upscaleTool === 'concat' && online && runtime.models
        ? selectConcatBackend(runtime.models.nodes ?? [])
        : null
      const errors = online && runtime.models
        ? [
            ...(upscaleTool === 'concat' && !concatBackend ? ['Node: MugenVideoPathLoader + MugenFFmpegConcat / Legacy Concat'] : []),
            ...requiredNodes.filter((nodeType) => !runtime.models?.nodes?.includes(nodeType)).map((nodeType) => `Node: ${nodeType}`),
            ...(upscaleTool === 'enhance' && !resolvedUpscaleModel ? [`Upscale Model: ${upscaleSettings.upscaleModel}`] : []),
            ...(interpolationNodes.includes('RIFE VFI') && !selectRifeModel(runtime.models.rife) ? [`RIFE Model: ${ENHANCE_RIFE_MODEL}`] : []),
          ]
        : [runtime?.error || 'Offline']
      return { server, runtime, errors, resolvedUpscaleModel, concatBackend }
    })
    const compatible = reports.filter((report) => report.errors.length === 0)
    if (!compatible.length) {
      setNotice(`Compatible Server がありません。\n${reports.map((report) => `${report.server.name}: ${report.errors.join(', ')}`).join('\n') || 'Server が未設定です。'}`)
      return
    }
    setSubmitting(true)
    setNotice('Preparing Jobs…')
    const resolvedUpscaleModels = new Map(compatible.map((report) => [report.server.id, report.resolvedUpscaleModel]))
    const concatBackends = new Map(compatible.flatMap((report) => report.concatBackend ? [[report.server.id, report.concatBackend] as const] : []))
    const assignments = distributeServers(compatible.map(({ server, runtime }) => ({ server, runtime })), batch).map((server, index) => ({
      server,
      jobId: crypto.randomUUID(),
      createdAt: Date.now() + index,
      batchIndex: index + 1,
      batchTotal: batch,
    }))
    const label = upscaleTool === 'enhance' ? 'Enhance' : 'Concat'
    const jobKind = upscaleTool === 'enhance' ? 'enhance' as const : 'concat' as const
    const sourceName = sourceFiles[0].name
    const submittedBy = displayIdentity(identity)
    setJobs((current) => [...current, ...assignments.map((assignment) => ({
      id: assignment.jobId,
      status: 'submitting' as const,
      progress: 0,
      currentNode: '',
      stepValue: 0,
      stepMax: 1,
      effectiveSeed: -1,
      segmentTotal: sourceFiles.length,
      createdAt: assignment.createdAt,
      serverId: assignment.server.id,
      serverName: assignment.server.name,
      label,
      sourceName,
      sourceCount: sourceFiles.length,
      jobKind,
      batchIndex: assignment.batchIndex,
      batchTotal: assignment.batchTotal,
      completedAt: undefined,
    }))])
    try {
      const uploadCache = new Map<string, Promise<string[]>>()
      const uploadsFor = (server: ServerDefinition) => {
        let pending = uploadCache.get(server.id)
        if (!pending) {
          pending = Promise.all(sourceFiles.map((file) => clientFor(server).uploadInput(file)))
          uploadCache.set(server.id, pending)
        }
        return pending
      }
      assignments.forEach((assignment) => { void uploadsFor(assignment.server).catch(() => undefined) })
      let queued = 0
      let failed = 0
      const usedServers = new Set<string>()
      for (const assignment of assignments) {
        try {
          const client = clientFor(assignment.server)
          const filenames = await uploadsFor(assignment.server)
          const workflow = upscaleTool === 'enhance'
            ? buildEnhanceWorkflow({
                filename: filenames[0],
                kind: enhanceKind,
                upscaleModel: resolvedUpscaleModels.get(assignment.server.id) ?? upscaleSettings.upscaleModel,
                scale: upscaleSettings.scale,
                ...(enhanceTargetSize ? { targetSize: { width: enhanceTargetSize.width, height: enhanceTargetSize.height } } : {}),
                targetFps: upscaleSettings.targetFps,
                rifeModel: selectRifeModel(runtimes[assignment.server.id]?.models?.rife ?? []) ?? ENHANCE_RIFE_MODEL,
                preserveAudio: preserveEnhanceAudio,
                sourceFps,
                interpolator,
                loop: upscaleSettings.enhanceLoop,
                gimmModel: selectGimmModel(runtimes[assignment.server.id]?.models?.gimm ?? []),
              })
            : buildConcatWorkflow({ filenames, fps: upscaleSettings.concatFps, crf: upscaleSettings.concatCrf, backend: concatBackends.get(assignment.server.id) ?? 'mugen' })
          const promptId = await client.submitWorkflow(workflow, {
            submittedBy,
            sourceName,
            submittedAt: assignment.createdAt,
            batchIndex: assignment.batchIndex,
            batchTotal: assignment.batchTotal,
            jobKind,
            sourceCount: sourceFiles.length,
          })
          queued += 1
          usedServers.add(assignment.server.id)
          trackPrompt(identity, {
            promptId,
            serverId: assignment.server.id,
            serverName: assignment.server.name,
            effectiveSeed: -1,
            segmentTotal: sourceFiles.length,
            createdAt: assignment.createdAt,
            sourceName,
            sourceCount: sourceFiles.length,
            jobKind,
            batchIndex: assignment.batchIndex,
            batchTotal: assignment.batchTotal,
          })
          setJobs((current) => current.map((job) => job.id === assignment.jobId ? { ...job, promptId, status: 'queued' } : job))
          void monitorJob(assignment.jobId, promptId, sourceFiles.length, assignment.server.id, assignment.server.name)
          void refreshServerRuntime(assignment.server)
        } catch (error) {
          failed += 1
          setJobs((current) => current.map((job) => job.id === assignment.jobId ? { ...job, status: 'error', error: displayError(error) } : job))
        }
      }
      setNotice(`${queued} ${upscaleTool === 'enhance' ? 'Enhance' : 'Concat'} Job${queued === 1 ? '' : 's'} queued across ${usedServers.size} Server${usedServers.size === 1 ? '' : 's'}${failed ? `・${failed} failed` : ''}・Generate is available again`)
      setTab('result')
    } finally {
      setSubmitting(false)
    }
  }

  const generate = async () => {
    if (workspaceMode === 'upscale') {
      await generateUpscale()
      return
    }
    persistDraft()
    const sourceSegments = profile.mode === 'simple' ? [profile.simple_segment] : profile.segments
    const missingEndImage = sourceSegments.find((segment) =>
      endModes[segment.id] === 'image' && !endFiles[segment.id] && !segment.end_image,
    )
    if (missingEndImage) {
      setNotice('End Image は「画像を指定」になっています。画像ファイルを選択してください。')
      return
    }
    const errors = validateGeneration(profile, connected, models, Boolean(startFile))
    if (errors.length) {
      setNotice(errors.join('\n'))
      return
    }
    const selectedServers = target === 'auto'
      ? servers.filter((server) => server.enabled)
      : servers.filter((server) => server.id === target && server.enabled)
    const reports = selectedServers.map((server) => {
      const runtime = runtimes[server.id]
      const online = runtime?.status === 'idle' || runtime?.status === 'busy'
      return {
        server,
        runtime,
        errors: online && runtime.models
          ? serverCompatibilityErrors(profile, runtime.models)
          : [runtime?.error || 'Offline'],
      }
    })
    const compatible = reports.filter((report) => report.errors.length === 0)
    if (!compatible.length) {
      setNotice(`Compatible Server がありません。\n${reports.map((report) => `${report.server.name}: ${report.errors.join(', ')}`).join('\n') || 'Server が未設定です。'}`)
      return
    }
    setSubmitting(true)
    setNotice('Preparing Jobs…')
    const distributedServers = distributeServers(compatible.map(({ server, runtime }) => ({ server, runtime })), batch)
    const sourceName = startFile?.name || profile.gs.start_image || 'Start Image'
    const submittedBy = displayIdentity(identity)
    const assignments = distributedServers.map((server, index) => {
      return {
        server,
        jobId: crypto.randomUUID(),
        seed: effectiveSeed(profile.gs.seed),
        createdAt: Date.now() + index,
        batchIndex: index + 1,
        batchTotal: distributedServers.length,
      }
    })
    setJobs((current) => [...current, ...assignments.map((assignment) => ({
      id: assignment.jobId,
      status: 'submitting' as const,
      progress: 0,
      currentNode: '',
      stepValue: 0,
      stepMax: 1,
      effectiveSeed: assignment.seed,
      segmentTotal: sourceSegments.length,
      createdAt: assignment.createdAt,
      serverId: assignment.server.id,
      serverName: assignment.server.name,
      sourceName,
      sourceCount: 1,
      jobKind: 'generation' as const,
      batchIndex: assignment.batchIndex,
      batchTotal: assignment.batchTotal,
    }))])
    try {
      const baseSegments = resolveGlobalLoras(profile)
      const preparedStart = startFile ? await prepareUploadFile(startFile) : null
      const preparedEnds = new Map<string, File>()
      for (const segment of baseSegments) {
        const file = endFiles[segment.id]
        if (file) preparedEnds.set(segment.id, await prepareUploadFile(file))
      }
      const uploadCache = new Map<string, Promise<{ startImage: string; endImages: Record<string, string> }>>()
      const uploadsFor = (server: ServerDefinition) => {
        let pending = uploadCache.get(server.id)
        if (!pending) {
          pending = (async () => {
            const client = clientFor(server)
            const startImage = preparedStart ? await client.uploadImage(preparedStart) : profile.gs.start_image
            const endImages: Record<string, string> = {}
            for (const [segmentId, file] of preparedEnds) endImages[segmentId] = await client.uploadImage(file)
            return { startImage, endImages }
          })()
          uploadCache.set(server.id, pending)
        }
        return pending
      }
      assignments.forEach((assignment) => { void uploadsFor(assignment.server).catch(() => undefined) })
      let queued = 0
      let failed = 0
      const usedServers = new Set<string>()
      for (const assignment of assignments) {
        try {
          const client = clientFor(assignment.server)
          const { startImage, endImages } = await uploadsFor(assignment.server)
          const segments = baseSegments.map((segment) => ({
            ...segment,
            ...(endImages[segment.id] ? { end_image: endImages[segment.id] } : {}),
          }))
          const gs = { ...profile.gs, start_image: startImage }
          const workflow = profile.mode === 'simple'
            ? buildSimpleWorkflow(gs, segments[0], { seed: assignment.seed })
            : buildWorkflow(gs, segments, { seed: assignment.seed })
          const promptId = await client.submitWorkflow(workflow, {
            submittedBy,
            sourceName,
            submittedAt: assignment.createdAt,
            batchIndex: assignment.batchIndex,
            batchTotal: assignment.batchTotal,
            jobKind: 'generation',
            sourceCount: 1,
          })
          queued += 1
          usedServers.add(assignment.server.id)
          setLastUsedSeed(assignment.seed)
          trackPrompt(identity, {
            promptId,
            serverId: assignment.server.id,
            serverName: assignment.server.name,
            effectiveSeed: assignment.seed,
            segmentTotal: segments.length,
            createdAt: assignment.createdAt,
            sourceName,
            sourceCount: 1,
            jobKind: 'generation',
            batchIndex: assignment.batchIndex,
            batchTotal: assignment.batchTotal,
          })
          setJobs((current) => current.map((job) => job.id === assignment.jobId ? { ...job, promptId, status: 'queued' } : job))
          void monitorJob(assignment.jobId, promptId, segments.length, assignment.server.id, assignment.server.name)
          void refreshServerRuntime(assignment.server)
        } catch (error) {
          failed += 1
          setJobs((current) => current.map((job) => job.id === assignment.jobId
            ? { ...job, status: 'error', error: displayError(error) }
            : job))
        }
      }
      setNotice(`${queued} Job${queued === 1 ? '' : 's'} queued across ${usedServers.size} Server${usedServers.size === 1 ? '' : 's'}${failed ? `・${failed} failed` : ''}・Generate is available again`)
      setTab('result')
    } catch (error) {
      setJobs((current) => current.map((job) => assignments.some((assignment) => assignment.jobId === job.id) && job.status === 'submitting'
        ? { ...job, status: 'error', error: displayError(error) }
        : job))
      setNotice(`Submit failed: ${displayError(error)}`)
    } finally {
      setSubmitting(false)
    }
  }

  const openLora = (scope: LoraScope) => {
    setLoraScope(scope)
    setTab('lora')
  }

  const updateSegments = (update: (segments: Segment[]) => Segment[]) => {
    setProfile((current) => ({ ...current, segments: update(current.segments) }))
  }

  const selectStartHistoryEntry = async (entry: StartImageHistoryEntry) => {
    const token = ++historySelectionToken.current
    setHistoryLoading(true)
    try {
      const file = await loadStartImage(identity, entry)
      if (token !== historySelectionToken.current) return
      if (!file) throw new Error('保存済み画像データが見つかりません。')
      setStartFile(file)
      setStartImageId(entry.id)
    } catch (error) {
      if (token === historySelectionToken.current) setNotice(`Start Image history failed: ${displayError(error)}`)
    } finally {
      if (token === historySelectionToken.current) setHistoryLoading(false)
    }
  }

  const stepStartHistory = (direction: -1 | 1) => {
    if (!startHistory.length) return
    const currentIndex = startHistory.findIndex((entry) => entry.id === startImageId)
    const baseIndex = currentIndex >= 0 ? currentIndex : direction === 1 ? -1 : 0
    const nextIndex = (baseIndex + direction + startHistory.length) % startHistory.length
    void selectStartHistoryEntry(startHistory[nextIndex])
  }

  const selectStartImage = async (file: File) => {
    const token = ++historySelectionToken.current
    setStartFile(file)
    setStartImageId('')
    setHistoryLoading(true)
    try {
      const result = await addStartImage(identity, file)
      if (token !== historySelectionToken.current) return
      setStartHistory(result.entries)
      setStartImageId(result.selectedId)
    } catch (error) {
      if (token === historySelectionToken.current) {
        setNotice(`Image history save failed: ${displayError(error)}・現在の画像はこの Session では使用できます。`)
      }
    } finally {
      if (token === historySelectionToken.current) setHistoryLoading(false)
    }
  }

  const clearAllStartImages = async () => {
    if (!startHistory.length) return
    if (!window.confirm(`Start Image の履歴 ${startHistory.length} 枚をすべて削除しますか？\nこの操作は元に戻せません。`)) return
    const token = ++historySelectionToken.current
    setHistoryLoading(true)
    try {
      await clearStartImageHistory(identity)
      if (token !== historySelectionToken.current) return
      setStartHistory([])
      setStartImageId('')
      setStartFile(null)
      setNotice('Start Image history cleared')
    } catch (error) {
      if (token === historySelectionToken.current) setNotice(`Image history clear failed: ${displayError(error)}`)
    } finally {
      if (token === historySelectionToken.current) setHistoryLoading(false)
    }
  }

  const updateEndFile = (segmentId: string, file?: File) => {
    setEndFiles((current) => {
      const next = { ...current }
      if (file) next[segmentId] = file
      else delete next[segmentId]
      return next
    })
    setEndModes((current) => {
      const next = { ...current }
      if (file) next[segmentId] = 'image'
      else delete next[segmentId]
      return next
    })
    if (identityReady) {
      void saveDraftEndImage(identity, segmentId, file)
        .catch((error) => setNotice(`End Image draft save failed: ${displayError(error)}`))
    }
  }

  const selectProfile = (name: string) => {
    forceNextDraftSave.current = true
    setProfileName(name)
    if (name && profiles[name]) {
      const selected = structuredClone(profiles[name])
      setProfile(selected)
      setWorkspaceMode(selected.mode)
      setStartFile(null)
      setStartImageId('')
      setEndFiles({})
      setEndModes({})
      void clearDraftEndImages(identity).catch(() => undefined)
    }
  }

  const saveProfile = () => {
    const requestedName = profileName || window.prompt('Profile name')?.trim() || ''
    if (!requestedName) return
    const name = profile.mode === 'simple' && !requestedName.endsWith('_simple')
      ? `${requestedName}_simple`
      : requestedName
    const next = { ...profiles, [name]: normalizeProfile(profile) }
    forceNextDraftSave.current = true
    setProfiles(next)
    setProfileName(name)
    writeProfiles(identity, next)
    if (identityReady && workspaceHydrated && draftSnapshotRef.current) {
      try {
        const savedDraft = { ...draftSnapshotRef.current, profileName: name }
        writeWorkDraft(identity, savedDraft)
        draftLastSaved.current = JSON.stringify(savedDraft)
        draftDirty.current = false
        forceNextDraftSave.current = false
        window.clearTimeout(draftTimer.current)
      } catch (error) {
        setNotice(`Profile saved・Workspace draft save failed: ${displayError(error)}`)
        return
      }
    }
    setNotice(`Profile '${name}' saved・connection data excluded`)
  }

  const deleteProfile = () => {
    if (!profileName || !profiles[profileName]) return
    if (!window.confirm(`Profile '${profileName}' を削除しますか？`)) return
    const next = { ...profiles }
    delete next[profileName]
    setProfiles(next)
    writeProfiles(identity, next)
    forceNextDraftSave.current = true
    setProfileName('')
  }

  const importProfile = async (file: File) => {
    try {
      const imported = normalizeProfile(JSON.parse(await file.text()))
      setProfile(imported)
      forceNextDraftSave.current = true
      setProfileName('')
      setStartFile(null)
      setStartImageId('')
      setEndFiles({})
      setEndModes({})
      void clearDraftEndImages(identity).catch(() => undefined)
      setNotice(`Imported '${file.name}'・save to keep it in this browser`)
    } catch (error) {
      setNotice(`Import failed: ${displayError(error)}`)
    }
  }

  const exportProfile = () => {
    const blob = new Blob([serializeProfile(profile)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${profileName || (profile.mode === 'simple' ? 'mugen2-profile_simple' : 'mugen2-profile')}.json`
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const commitServers = (next: ServerDefinition[]) => {
    setServers(next)
    persistServerRegistry(identity, next)
  }

  const updateServer = (id: string, patch: Partial<ServerDefinition>) => {
    const currentServer = servers.find((server) => server.id === id)
    if (!currentServer) return
    const urlChange = patch.url !== undefined && patch.url !== currentServer.url
    const connectionChange = urlChange || patch.enabled !== undefined
    const hasActiveJob = jobs.some((job) => job.serverId === id && (job.status === 'submitting' || job.status === 'queued' || job.status === 'running'))
    if (connectionChange && hasActiveJob) {
      setNotice(`Server '${currentServer.name}' に active Job があるため、URL／Enabled は変更できません。`)
      return
    }
    const next = servers.map((server) => server.id === id ? { ...server, ...patch } : server)
    if (connectionChange) {
      clients.current.get(id)?.configure('', '')
      if (urlChange) deleteServerModelCache(identity, id)
      setRuntimes((current) => ({ ...current, [id]: {
        status: 'disconnected',
        running: 0,
        pending: 0,
        models: urlChange ? undefined : current[id]?.models,
      } }))
    }
    commitServers(next)
  }

  const addServer = (source: 'personal' | 'temporary') => {
    const created = createServer(source)
    const next = [...servers, created]
    commitServers(next)
    setTab('settings')
    if (created.url) void connectServers([created], true)
  }

  const moveServer = (id: string, direction: -1 | 1) => {
    const index = servers.findIndex((server) => server.id === id)
    const targetIndex = index + direction
    if (index < 0 || targetIndex < 0 || targetIndex >= servers.length) return
    const next = [...servers]
    ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
    commitServers(next)
  }

  const deleteServer = (id: string) => {
    const server = servers.find((item) => item.id === id)
    if (!server || server.source === 'company') return
    if (jobs.some((job) => job.serverId === id && (job.status === 'submitting' || job.status === 'queued' || job.status === 'running'))) {
      setNotice(`Server '${server.name}' に active Job があるため削除できません。`)
      return
    }
    if (!window.confirm(`Server '${server.name}' を削除しますか？`)) return
    clients.current.get(id)?.configure('', '')
    clients.current.delete(id)
    deleteServerModelCache(identity, id)
    const next = servers.filter((item) => item.id !== id)
    commitServers(next)
    setRuntimes((current) => { const copy = { ...current }; delete copy[id]; return copy })
    const nextApiKeys = { ...apiKeysRef.current }
    delete nextApiKeys[id]
    apiKeysRef.current = nextApiKeys
    setApiKeys(nextApiKeys)
    if (target === id) setTarget('auto')
  }

  const updateApiKey = (id: string, value: string) => {
    const server = servers.find((item) => item.id === id)
    if (!server) return
    if (jobs.some((job) => job.serverId === id && (job.status === 'submitting' || job.status === 'queued' || job.status === 'running'))) {
      setNotice(`Server '${server.name}' に active Job があるため API key は変更できません。`)
      return
    }
    const nextApiKeys = { ...apiKeysRef.current, [id]: value }
    apiKeysRef.current = nextApiKeys
    setApiKeys(nextApiKeys)
    clients.current.get(id)?.configure(server.url, value)
    setRuntimes((current) => ({ ...current, [id]: {
      ...current[id],
      status: 'disconnected',
      running: 0,
      pending: 0,
      error: undefined,
    } }))
  }

  const updateNotifications = (patch: Partial<NotificationPreferences>) => {
    const next = { ...notifications, ...patch }
    setNotifications(next)
    writeNotificationPreferences(identity, next)
  }

  const testSound = async () => {
    try {
      await playCompletionSound(identity, { ...notifications, soundEnabled: true })
      setNotice('Notification sound played')
    } catch (error) {
      setNotice(`Sound failed: ${displayError(error)}`)
    }
  }

  const selectCustomSound = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      setNotice('Custom sound は 5 MB 以下にしてください。')
      return
    }
    try {
      await saveCustomSound(identity, file)
      updateNotifications({ soundSource: 'custom', customSoundName: file.name, soundEnabled: true })
      setNotice(`Custom sound '${file.name}' saved in this browser`)
    } catch (error) {
      setNotice(`Custom sound failed: ${displayError(error)}`)
    }
  }

  const setDesktopEnabled = async (enabled: boolean) => {
    if (!enabled) {
      updateNotifications({ desktopEnabled: false })
      return
    }
    const permission = await requestDesktopPermission()
    updateNotifications({ desktopEnabled: permission === 'granted' })
    setNotice(permission === 'granted'
      ? 'Desktop notifications enabled'
      : permission === 'denied' ? 'Desktop notification permission was denied by the browser' : 'Desktop notifications are unavailable')
  }

  const downloadJob = async (job: JobRecord) => {
    const server = servers.find((item) => item.id === job.serverId)
    if (!server || !job.media) {
      setNotice('Download failed: Job の Server または動画情報が見つかりません。')
      return
    }
    try {
      await clientFor(server).download(job.media)
    } catch (error) {
      setNotice(`Download failed: ${displayError(error)}`)
    }
  }

  const selectResultJob = async (job: JobRecord) => {
    setActiveJobId(job.id)
    if (job.mediaUrl || !job.media) return
    const server = servers.find((item) => item.id === job.serverId)
    if (!server) return
    try {
      const mediaUrl = await clientFor(server).playableUrl(job.media)
      setJobs((current) => current.map((item) => item.id === job.id ? { ...item, mediaUrl } : item))
    } catch (error) {
      setNotice(`Result preview failed: ${displayError(error)}`)
    }
  }

  const changeWorkspaceMode = (mode: WorkspaceMode) => {
    if (mode === 'seedance') return
    setWorkspaceMode(mode)
    if (mode === 'multi' || mode === 'simple') {
      setProfile((current) => ({ ...current, mode }))
      setProfileName('')
    }
  }

  if (!identityReady || !workspaceHydrated) {
    return <div className="app-loading" role="status"><span className="brand-mark">M</span><strong>Restoring workspace…</strong></div>
  }

  return (
    <div className="app-shell" lang="ja">
      <ConnectionBar servers={servers} runtimes={runtimes} target={target} connecting={connecting} profiles={profiles} profileName={profileName} profileDirty={profileDirty} mode={profile.mode} onTarget={setTarget} onConnect={() => void connect()} onProfileSelect={selectProfile} onSaveProfile={saveProfile} />
      <main className="workspace">
        {workspaceMode === 'upscale'
          ? <UpscaleQuickPanel mode={workspaceMode} tool={upscaleTool} settings={upscaleSettings} sourceSize={enhanceSourceSize} models={models} connected={connected} submitting={submitting} batch={batch} onMode={changeWorkspaceMode} onTool={setUpscaleTool} onSettings={(patch) => { if (patch.enhanceSource && enhanceDetectionRef.current) enhanceDetectionRef.current.manual = true; setUpscaleSettings((current) => ({ ...current, ...patch })) }} onBatch={setBatch} onGenerate={() => void generate()} />
          : <QuickPanel profile={profile} models={models} connected={connected} startPreview={startPreview} startName={startFile?.name ?? profile.gs.start_image} startHistory={startHistory} startImageId={startImageId} historyLoading={historyLoading} batch={batch} lastUsedSeed={lastUsedSeed} submitting={submitting} workspaceMode={workspaceMode} onWorkspaceMode={changeWorkspaceMode} onStartImage={(file) => void selectStartImage(file)} onHistoryStep={stepStartHistory} onClearHistory={() => void clearAllStartImages()} onGs={updateGs} onBatch={setBatch} onGenerate={() => void generate()} />}
        <section className="center-panel">
          {workspaceMode === 'upscale'
            ? <UpscaleCenter tool={upscaleTool} enhanceFile={enhanceFile} concatFiles={concatFiles} onEnhanceFile={setEnhanceFile} onConcatFiles={setConcatFiles} />
            : <Timeline profile={profile} endFiles={endFiles} endModes={endModes} onSegments={updateSegments} onSimple={(simple_segment) => setProfile((current) => ({ ...current, simple_segment }))} onOpenLora={openLora} onEndMode={(segmentId, mode) => setEndModes((current) => ({ ...current, [segmentId]: mode }))} onEndFile={updateEndFile} />}
          <SystemBar message={notice} />
        </section>
        <Inspector tab={tab} onTab={setTab} profile={profile} models={models} loraScope={loraScope} autoPair={autoPair} connected={connected} jobs={jobs} activeJobId={activeJobId} compactResultPreview={compactResultPreview} profileName={profileName} profiles={profiles} notifications={notifications} servers={servers} runtimes={runtimes} apiKeys={apiKeys} onProfile={setProfile} onGs={updateGs} onAutoPair={setAutoPair} onReturnGlobal={() => { setLoraScope({ kind: 'global' }); setTab('result') }} onSelectResult={(job) => void selectResultJob(job)} onDownload={(job) => void downloadJob(job)} onCancelJob={(job) => void cancelJob(job)} onClearFinishedJobs={clearFinishedJobs} onCompactResultPreview={setCompactResultPreview} onNotifications={updateNotifications} onTestSound={() => void testSound()} onCustomSound={(file) => void selectCustomSound(file)} onDesktopEnabled={(enabled) => void setDesktopEnabled(enabled)} onAddServer={addServer} onServer={updateServer} onMoveServer={moveServer} onDeleteServer={deleteServer} onApiKey={updateApiKey} onConnectServer={connectServerById} onDeleteProfile={deleteProfile} onImportProfile={(file) => void importProfile(file)} onExportProfile={exportProfile} />
      </main>
    </div>
  )
}
